import React, { useState, useEffect, useMemo } from "react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { resolveBuildUpEvaluateCode } from "../../utils/foulBuildUpEvaluateCodesMapping";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Typography,
  Alert,
  Space,
  Modal,
  Tag,
  Tooltip,
  Segmented,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useGlobalSettings } from "../../hooks/useGlobalSettings";
import { COLORS, formatUnit } from "../../utils/constants";
import { formatCurrency } from "../../utils/calculations";
import {
  BACKFILL_TYPE_OPTIONS,
  backfillLabel,
  backfillEvalCode,
  migrateBackfillType,
} from "../../utils/backfillTypes";
import {
  buildFoulSurplusBackfillData,
  type FoulSurplusBackfillRow,
  type FoulFormulaInfo,
} from "../../utils/foulSummaryUtils";
import MainLayout from "../layout/MainLayout";
import {
  Manhole,
  SewerPipeRun,
  LateralConnection,
  FoulRoadConstConfig,
  PumpChamberConfig,
  FoulBuildUpRowData,
} from "../../types/tender.types";
import {
  calculateTotalPipeLength,
  calculateManholeScheduleSummary,
  calculatePipeRunsScheduleSummary,
} from "../../utils/sewersCalculations";
import {
  calculateFoulSewersSummary,
  calculateManholesSummary,
} from "../../utils/sewerSummaryCalculations";
import { calculateConnectionSummary } from "../../utils/connectionCalculations";
import { BuildUpRowDef, PUMP_STATION_ROWS, RISING_MAIN_ROWS, applyPumpStationLadderType } from "../../utils/foulBuildUpRows";
import {
  calculateGroupedManholeRates,
  getTotalGroupedValue,
  ManholeRateItem,
} from "../../utils/rateCalculations";
import {
  exportManholeRatesToExcel,
  exportManholeRatesSummary,
} from "../../utils/manholeRatesExport";
import {
  exportFoulSewersToExcel,
  importFoulSewersFromExcel,
  generateFoulSewersTemplate,
  FoulSewersExcelData,
} from "../../utils/foulSewersExcelUtils";
import { pipeworkRatesByDiameter, lateralRates } from "../../mock";
import { ExcelImportModal } from "../common/ExcelImportExport";
import { ColumnConfigModal, ConfigField, ConfigSection } from "../common/ColumnConfigModal";
import NumberInput from "../common/NumberInput";
import ExpandCollapseAllButton from "../common/ExpandCollapseAllButton";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";


// Same option list as Compound.tsx's/groundworks/foundations' "Mesh Reinforcement" dropdowns.
const MESH_TYPE_OPTIONS = ['A142', 'A193', 'A252', 'A393', 'B283', 'B385', 'B503', 'B785', 'B1131'];

// Same option list as Compound.tsx's/groundworks/foundations' "Concrete to Strip" dropdowns.
const CONCRETE_GRADE_OPTIONS = ['GEN1', 'GEN2', 'GEN3', 'FND2', 'FND3z', 'FND4', 'RC28', 'RC35', 'RC32', 'RC40', 'C16', 'C20'];

// Component to display manhole, pipework and lateral rates in the modal
const RatesTable: React.FC<{
  manholes: Manhole[];
  pipeRuns: SewerPipeRun[];
  laterals: LateralConnection[];
  projectName?: string;
}> = ({ manholes, pipeRuns, laterals, projectName }) => {
  const diameterGroups = calculateGroupedManholeRates(manholes);
  const totalManholeValue = getTotalGroupedValue(diameterGroups);

  // Calculate pipework rates - show all rates from mock data
  const calculatePipeworkRates = () => {
    const pipeworkData: any[] = [];

    // Show all rates from pipeworkRatesByDiameter mock data
    Object.entries(pipeworkRatesByDiameter).forEach(([diameter, rates]) => {
      rates.forEach((rate) => {
        // Find matching pipe runs for this rate
        const matchingPipeRuns = pipeRuns.filter((pipeRun) => {
          const runDiameter = pipeRun.diameter;
          const runDepthString = `${pipeRun.runDepthCategory.toFixed(2)}m`;
          return runDiameter === diameter && runDepthString === rate.depth;
        });

        if (matchingPipeRuns.length > 0) {
          // If there are matching pipe runs, show combined quantity
          const totalQty = matchingPipeRuns.reduce(
            (sum, run) => sum + run.length,
            0
          );
          pipeworkData.push({
            item: rate.name,
            description: rate.name,
            qty: totalQty,
            unit: rate.unit,
            rate: rate.rate,
            amount: totalQty * rate.rate,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        } else {
          // Show the rate with 0 quantity if no matching pipe runs
          pipeworkData.push({
            item: rate.name,
            description: rate.name,
            qty: 0,
            unit: rate.unit,
            rate: rate.rate,
            amount: 0,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        }
      });
    });

    return pipeworkData;
  };

  const pipeworkRates = calculatePipeworkRates();
  const totalPipeworkValue = pipeworkRates.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  // Calculate lateral rates - show all rates from mock data
  const calculateLateralRates = () => {
    const lateralData: any[] = [];

    // Show all rates from lateralRates mock data
    Object.entries(lateralRates).forEach(([diameter, rates]) => {
      rates.forEach((rate) => {
        // Skip rates with null depth (headers) or null rate (non-applicable)
        if (rate.depth === null || rate.rate === null) {
          return;
        }

        // Find matching lateral connections for this rate
        const matchingLaterals = laterals.filter((lateral) => {
          const lateralDiameter = lateral.pipeDiameter;
          const lateralDepthString = `${(lateral.avgDepthCategory || 0).toFixed(
            2
          )}m`;
          // Map lateral diameter to available rates (100mm -> 150mm for now)
          const mappedDiameter =
            lateralDiameter === "100mm" ? "150mm" : lateralDiameter;
          return (
            mappedDiameter === diameter && lateralDepthString === rate.depth
          );
        });

        if (matchingLaterals.length > 0) {
          // If there are matching laterals, show combined quantity
          const totalQty = matchingLaterals.reduce(
            (sum, lateral) => sum + lateral.length,
            0
          );
          lateralData.push({
            item: rate.name,
            description: rate.name,
            qty: totalQty,
            unit: rate.unit,
            rate: rate.rate,
            amount: totalQty * rate.rate,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        } else {
          // Show the rate with 0 quantity if no matching laterals
          lateralData.push({
            item: rate.name,
            description: rate.name,
            qty: 0,
            unit: rate.unit,
            rate: rate.rate,
            amount: 0,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        }
      });
    });

    return lateralData;
  };

  const lateralRatesData = calculateLateralRates();
  const totalLateralValue = lateralRatesData.reduce(
    (sum, item) => sum + item.amount,
    0
  );
  const grandTotal = totalManholeValue + totalPipeworkValue + totalLateralValue;

  const handleExportFull = () => {
    try {
      exportManholeRatesToExcel(
        diameterGroups,
        manholes,
        {
          projectName: projectName || "Untitled Project",
          exportDate: new Date(),
        },
        pipeworkRates,
        pipeRuns
      );
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleExportSummary = () => {
    try {
      exportManholeRatesSummary(
        diameterGroups,
        manholes,
        {
          projectName: projectName || "Untitled Project",
          exportDate: new Date(),
        },
        pipeworkRates,
        pipeRuns
      );
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Flatten manhole groups into table data with diameter headers
  const manholeTableData: (ManholeRateItem & {
    isDiameterHeader?: boolean;
    subtotal?: number;
  })[] = [];

  diameterGroups.forEach((group) => {
    // Add diameter header row
    manholeTableData.push({
      item: -1,
      description: `${group.diameter} Diameter Manholes`,
      qty: 0,
      unit: "",
      rate: 0,
      amount: 0,
      diameter: group.diameter,
      exactMatch: true,
      isDiameterHeader: true,
      subtotal: group.subtotal,
    });

    // Add items for this diameter
    group.items.forEach((item) => {
      manholeTableData.push(item);
    });
  });

  // Group pipework data by diameter and add proper serialization
  const pipeworkGroups = pipeworkRates.reduce((acc, item) => {
    if (!acc[item.diameter]) {
      acc[item.diameter] = [];
    }
    acc[item.diameter].push(item);
    return acc;
  }, {} as Record<string, any[]>);

  const pipeworkTableData: any[] = [];
  let pipeworkItemCounter = 1;

  Object.entries(pipeworkGroups).forEach(([diameter, items]) => {
    // Add diameter header row
    pipeworkTableData.push({
      item: -1,
      description: `${diameter} Diameter Pipework`,
      qty: 0,
      unit: "",
      rate: 0,
      amount: 0,
      isDiameterHeader: true,
      subtotal: items.reduce((sum, item) => sum + item.amount, 0),
    });

    // Add individual items with proper serialization
    items.forEach((item) => {
      pipeworkTableData.push({
        ...item,
        item: pipeworkItemCounter++,
      });
    });
  });

  const columns = [
    {
      title: "Item",
      dataIndex: "item",
      key: "item",
      width: 60,
      render: (item: number, record: any) => {
        if (record.isDiameterHeader) return "";
        return item;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 350,
      render: (description: string, record: any) => {
        if (record.isDiameterHeader) {
          return <strong className="text-blue-600">{description}</strong>;
        }
        return (
          <div>
            <div>{description}</div>
            {!record.exactMatch && (
              <Tag color="orange" size="small">
                Closest Match
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 60,
      align: "center" as const,
      render: (qty: number, record: any) => {
        if (record.isDiameterHeader) return "";
        return qty > 0 ? qty : "";
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 60,
      align: "center" as const,
      render: (unit: string, record: any) => {
        if (record.isDiameterHeader) return "";
        return formatUnit(unit);
      },
    },
    {
      title: "Rate (£)",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      align: "right" as const,
      render: (rate: number, record: any) => {
        if (record.isDiameterHeader) return "";
        return `£${rate.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      title: "Amount (£)",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right" as const,
      render: (amount: number, record: any) => {
        if (record.isDiameterHeader) {
          return record.subtotal > 0 ? (
            <strong className="text-blue-600">
              £
              {record.subtotal.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          ) : (
            ""
          );
        }
        return amount > 0 ? (
          <span className="font-semibold">
            £
            {amount.toLocaleString("en-GB", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ) : (
          ""
        );
      },
    },
  ];

  return (
    <div>
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <Typography.Title level={4} className="m-0">
            Rate Breakdown
          </Typography.Title>
          <div className="text-right">
            <Typography.Text className="block">
              Manhole Total: £{totalManholeValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text className="block">
              Pipework Total: £{totalPipeworkValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text className="block">
              Lateral Total: £{totalLateralValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text strong className="text-lg block">
              Grand Total: £{grandTotal.toFixed(2)}
            </Typography.Text>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <Space>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportFull}
          >
            Export Full Details
          </Button>
          <Button
            type="default"
            icon={<FileExcelOutlined />}
            onClick={handleExportSummary}
          >
            Export Summary
          </Button>
        </Space>
      </div>

      {/* Manholes Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Manholes (£{totalManholeValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={manholeTableData}
          columns={columns}
          rowKey={(record, index) =>
            record.isDiameterHeader
              ? `manhole-header-${record.diameter}`
              : `manhole-item-${record.item}-${index}`
          }
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
          rowClassName={(record) =>
            record.isDiameterHeader ? "bg-blue-50 font-semibold" : ""
          }
        />
      </div>
      {/* Pipework Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Pipework (£{totalPipeworkValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={pipeworkTableData}
          columns={columns}
          rowKey={(record, index) =>
            record.isDiameterHeader
              ? `pipework-header-${record.description}`
              : `pipework-item-${record.item}-${index}`
          }
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
          rowClassName={(record) =>
            record.isDiameterHeader ? "bg-blue-50 font-semibold" : ""
          }
        />
      </div>

      {/* Lateral Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Lateral Connections (£{totalLateralValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={lateralRatesData.map((item, index) => ({
            ...item,
            item: index + 1,
          }))}
          columns={columns}
          rowKey={(record, index) => `lateral-item-${record.item}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        />
      </div>

      {manholes.length === 0 && (
        <Alert
          message="No manholes available to calculate rates."
          type="info"
          showIcon
          className="mt-4"
        />
      )}
    </div>
  );
};

// ── Rocker quantity helper ────────────────────────────────────────────────────
// Sums rockers for all pipe runs where pipeType matches material (case-insensitive)
// and the numeric part of diameter matches pipeDiameter.
// Equivalent to: SUMIFS('Foul Take off'!O:O, J:J, material, K:K, pipeDiameter)
const calcRockerQty = (
  pipeRuns: SewerPipeRun[],
  material: string,
  pipeDiameter: number,
): number =>
  pipeRuns
    .filter(
      (run) =>
        (run.pipeType ?? '').toLowerCase() === material.toLowerCase() &&
        parseInt(run.diameter ?? '', 10) === pipeDiameter,
    )
    .reduce((sum, run) => sum + (run.rockers ?? 0), 0);

// Sums concreteProtection metres for all pipe runs with a matching numeric diameter.
// Equivalent to: SUMIF('Foul Take off'!K:K, pipeDiameter, N:N)
const calcConcreteProtection = (
  pipeRuns: SewerPipeRun[],
  pipeDiameter: number,
): number =>
  pipeRuns
    .filter((run) => parseInt(run.diameter ?? '', 10) === pipeDiameter)
    .reduce((sum, run) => sum + (run.concreteProtection ?? 0), 0);

// Ladder metres = (depth - 0.15) per manhole where depthCategory > 2.25m and material matches.
// SUM of J column (ladder metres) across all manholes
const calcLadderMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.ladders ?? 0), 0);

// SUM of K column (safety chain metres) across all manholes
const calcChainMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.safetyChains ?? 0), 0);

// ── Static foul summary bill template data ────────────────────────────────────

const FOUL_STUB_PIPE_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; dia: number }>;
}> = [
  {
    key: 'fstub-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100R', desc: 'EO for 100mmØ Stub / rocker pipe', dia: 100 },
      { code: 'APS150R', desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APS225R', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APS300R', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'fstub-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APU300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'fstub-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150SR', desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APRS225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APRS300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
      { code: 'APRS400SR', desc: 'EO for 400mmØ Stub / rocker pipe', dia: 400 },
      { code: 'APRS450SR', desc: 'EO for 450mmØ Stub / rocker pipe', dia: 450 },
      { code: 'APRS500SR', desc: 'EO for 500mmØ Stub / rocker pipe', dia: 500 },
      { code: 'APRS600SR', desc: 'EO for 600mmØ Stub / rocker pipe', dia: 600 },
      { code: 'APRS750SR', desc: 'EO for 750mmØ Stub / rocker pipe', dia: 750 },
    ],
  },
  {
    key: 'fstub-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
      { code: 'APM450SR', desc: 'EO for 450mmØ Stub / rocker pipe', dia: 450 },
      { code: 'APM525SR', desc: 'EO for 525mmØ Stub / rocker pipe', dia: 525 },
      { code: 'APM600SR', desc: 'EO for 600mmØ Stub / rocker pipe', dia: 600 },
      { code: 'APM675SR', desc: 'EO for 675mmØ Stub / rocker pipe', dia: 675 },
    ],
  },
];

const FOUL_JUNCTION_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; mainDia: string; branchDia: string }>;
}> = [
  {
    key: 'fjunc-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100J1', desc: 'EO for 100 x 100mm junction', mainDia: '100mm', branchDia: '100mm' },
      { code: 'APS150J1', desc: 'EO for 150 x 100mm junction', mainDia: '150mm', branchDia: '100mm' },
      { code: 'APS150J2', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APS225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APS225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APS225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APS300J1', desc: 'EO for 300 x 100mm junction', mainDia: '300mm', branchDia: '100mm' },
      { code: 'APS300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APS300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APS300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'fjunc-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APU225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APU225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APU300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APU300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APU300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'fjunc-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150J1', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APRS225J1', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APRS300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APRS400J1', desc: 'EO for 400 x 150mm junction', mainDia: '400mm', branchDia: '150mm' },
      { code: 'APRS450J1', desc: 'EO for 450mm Saddle',          mainDia: '450mm', branchDia: '' },
      { code: 'APRS500J1', desc: 'EO for 500mm Saddle',          mainDia: '500mm', branchDia: '' },
      { code: 'APRS600J1', desc: 'EO for 600mm Saddle',          mainDia: '600mm', branchDia: '' },
      { code: 'APRS750J1', desc: 'EO for 750mm Saddle',          mainDia: '750mm', branchDia: '' },
    ],
  },
  {
    key: 'fjunc-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APM375J1', desc: 'EO for 375 x 150mm junction', mainDia: '375mm', branchDia: '150mm' },
      { code: 'APM450J1', desc: 'EO for 450 x 150mm junction', mainDia: '450mm', branchDia: '150mm' },
      { code: 'APMCD001', desc: 'Core Drill 525Ø+',             mainDia: '525mm', branchDia: '' },
    ],
  },
];

const FOUL_EO_CONC_BED_ITEMS = [
  { code: 'AMHEO027', desc: 'EO concrete bed and surround 150mmØ pipe' },
  { code: 'AMHEO028', desc: 'EO concrete bed and surround 225mmØ pipe' },
  { code: 'AMHEO029', desc: 'EO concrete bed and surround 300mmØ pipe' },
  { code: 'AMHEO030', desc: 'EO concrete bed and surround 375mmØ pipe' },
  { code: 'AMHEO031', desc: 'EO concrete bed and surround 450mmØ pipe' },
  { code: 'AMHEO032', desc: 'EO concrete bed and surround 525mmØ pipe' },
  { code: 'AMHEO033', desc: 'EO concrete bed and surround 600mmØ pipe' },
  { code: 'AMHEO034', desc: 'EO concrete bed and surround 675mmØ pipe' },
];

const FOUL_CONC_PROTECTION_ITEMS: Array<{ code: string; desc: string; dia: number }> = [
  { code: 'AMHEO008', desc: 'Concrete protection Slab 150mmØ', dia: 150 },
  { code: 'AMHEO009', desc: 'Concrete protection Slab 225mmØ', dia: 225 },
  { code: 'AMHEO010', desc: 'Concrete protection Slab 300mmØ', dia: 300 },
  { code: 'AMHEO012', desc: 'Concrete protection Slab 375mmØ', dia: 375 },
  { code: 'AMHEO013', desc: 'Concrete protection Slab 450mmØ', dia: 450 },
  { code: 'AMHEO014', desc: 'Concrete protection Slab 525mmØ', dia: 525 },
  { code: 'AMHEO015', desc: 'Concrete protection Slab 600mmØ', dia: 600 },
  { code: 'AMHEO016', desc: 'Concrete protection Slab 675mmØ', dia: 675 },
];

const FOUL_EO_ITEMS = [
  { code: 'AMHEO006',  desc: 'EO for working in offsite carriageway',                         unit: 'm'  },
  { code: 'AMHEO007',  desc: 'EO trench reinstatement in carriageway',                        unit: 'm'  },
  { code: 'AMHEO003B', desc: 'EO construct manhole in existing carriageway',                  unit: 'Nr' },
  { code: 'AMHEO003',  desc: 'EO Breaking into existing manhole for connection',              unit: 'Nr' },
  { code: 'AMHEO003A', desc: 'EO Construct manhole on existing sewer',                       unit: 'Nr' },
  { code: 'ALP031',    desc: 'EO for impermeable geotextile membrane wrap to 150mmØ pipe',   unit: 'm'  },
];

const FOUL_REDUNDANT_SEWER_ITEMS = [
  { desc: 'Grout / grub up redundant sewer - n ex 225mmØ', unit: 'm'  },
  { desc: 'Break out existing manhole, remove from site',   unit: 'Nr' },
];

// ─── Excel formula helpers (pure, no React deps) ────────────────────────────

function calcFoulManholeDerived(
  mh: Manhole,
  ladderCfg: 'N/A' | 'Stainless' | 'Galvanised',
  safetyChainCfg: 'N/A' | 'Stainless' | 'Galvanised',
): Manhole {
  const depth = parseFloat((mh.coverLevel - mh.invertLevel).toFixed(3));
  const depthCategory = Math.round(depth / 0.25) * 0.25;
  // Ladders/chains = Cover Level − Invert Level − 0.15 when depth category > 2.25; else 0
  const ladderVal = parseFloat((mh.coverLevel - mh.invertLevel - 0.15).toFixed(3));
  const ladders = ladderCfg === 'N/A' ? 0 : depthCategory > 2.25 ? ladderVal : 0;
  const safetyChains = safetyChainCfg === 'N/A' ? 0 : depthCategory > 2.25 ? ladderVal : 0;
  // Easi/Trad: =IF(diameter="","",IF(ISNUMBER(MATCH(diameter,{1200,1500,1800},0)),"Easi","Trad"))
  const diamNum = parseInt((mh.diameter ?? '').replace(/mm$/i, ''), 10);
  const easiTrad: 'easi' | 'trad' | undefined = !mh.diameter
    ? undefined
    : [1200, 1500, 1800].includes(diamNum) ? 'easi' : 'trad';
  return { ...mh, depth, depthCategory, ladders, safetyChains, easiTrad };
}

function calcPipeRunDerived(run: SewerPipeRun, manholes: Manhole[]): SewerPipeRun {
  // No `?? run.depthFrom` fallback here: depthFrom/depthTo are read-only,
  // fully derived from the selected manhole — if fromMH/toMH doesn't match any
  // manhole (cleared, or renamed out from under it), the depth must reset to 0
  // rather than keep showing the previous manhole's stale calculated value.
  const depthFrom = manholes.find(m => m.ref === run.fromMH)?.depth ?? 0;
  const depthTo   = manholes.find(m => m.ref === run.toMH)?.depth   ?? 0;
  const roadConst = run.roadConstDepth ?? 0;
  const averageDepth     = parseFloat(((depthFrom + depthTo) / 2 - roadConst).toFixed(3));
  const runDepthCategory = Math.round(averageDepth / 0.25) * 0.25;
  const diamNum          = parseInt(run.diameter) || 0;
  // N: IF(runDepthCategory - diameter_m <= 1.25, length, 0)
  const concreteProtection = runDepthCategory - diamNum / 1000 <= 1.25 ? (run.length || 0) : 0;
  // O: IF(diameter > 0, 2, 0)
  const rockers = diamNum > 0 ? 2 : 0;
  return { ...run, depthFrom, depthTo, averageDepth, runDepthCategory, concreteProtection, rockers };
}

function calcLateralDerived(
  lat: LateralConnection,
  manholes: Manhole[],
  pairedRunDepthCategory: number,
): LateralConnection {
  const demarcDepth         = parseFloat(((lat.demarcCoverLevel ?? 0) - (lat.demarcInvertLevel ?? 0)).toFixed(3));
  const demarcDepthCategory = Math.round(demarcDepth / 0.25) * 0.25;
  // AB: IF(junctionOffRun empty, (runDepthCat + demarcDepthCat)/2, (runDepthCat + lookup MH depthCat)/2)
  const hasJunction = lat.junctionOffRun && lat.junctionOffRun > 0;
  let aveRunDepth: number;
  if (hasJunction) {
    const mhDepthCat = manholes.find(m => m.ref === lat.toManhole)?.depthCategory ?? 0;
    aveRunDepth = (pairedRunDepthCategory + mhDepthCat) / 2;
  } else {
    aveRunDepth = (pairedRunDepthCategory + demarcDepthCategory) / 2;
  }
  const averageDepth    = parseFloat(aveRunDepth.toFixed(3));
  const avgDepthCategory = Math.round(averageDepth / 0.25) * 0.25;
  return { ...lat, demarcDepth, demarcDepthCategory, averageDepth, avgDepthCategory };
}

// ─────────────────────────────────────────────────────────────────────────────

const FoulSewersTakeoff: React.FC = () => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: 'smooth', delay: 100 });
  const { state, updateField, saveTender } = useTender();
  const { settings } = useGlobalSettings();
  useAutoSave();
  const navigate = useNavigate();

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code columns are actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    // A few evaluate codes are combined (e.g. "ALP050+ALP031") — sum whichever
    // parts resolve to a library rate rather than requiring an exact combined match.
    if (code.includes('+')) {
      const parts = code.split('+').map((c) => resourceCodeMap.get(c.trim().toUpperCase())).filter((r): r is number => r !== undefined);
      return parts.length > 0 ? parts.reduce((sum, r) => sum + r, 0) : undefined;
    }
    return resourceCodeMap.get(code.trim().toUpperCase());
  };

  const handleSaveAndContinue = () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    navigate("/roads-sewers/storm-sewers");
  };

  const foulSewersData = state.foulSewers;

  const [activeTab, setActiveTab] = useState("manholes");
  // Defers this step's per-manhole/per-run tables' mount by one paint cycle
  // so a tender with a lot of manholes/pipe runs doesn't make the tab look
  // frozen while the table's render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Re-keyed on `activeTab` since
  // switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the table(s) render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);
  const [stickyOffset, setStickyOffset] = useState(310);
  const [selectedManholeKeys, setSelectedManholeKeys] = useState<React.Key[]>([]);
  const [selectedCombinedKeys, setSelectedCombinedKeys] = useState<React.Key[]>([]);

  // Shared row-delete confirmation — matches the DeleteConfirmationModal pattern
  // used by Groundworks tables, so every delete action in this page asks for
  // confirmation the same way instead of deleting immediately on click. The
  // optional count customises the modal message for bulk (multi-row) deletes.
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<(() => void) | null>(null);
  const [pendingDeleteCount, setPendingDeleteCount] = useState(1);
  const requestDelete = (action: () => void, count: number = 1) => {
    setPendingDelete(() => action);
    setPendingDeleteCount(count);
    setDeleteModalVisible(true);
  };
  const confirmDelete = () => {
    pendingDelete?.();
    setDeleteModalVisible(false);
    setPendingDelete(null);
  };
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setPendingDelete(null);
  };
  const [editingCell, setEditingCell] = useState<{
    table: string;
    id: string;
    field: string;
  } | null>(null);
  const [ratesModalVisible, setRatesModalVisible] = useState(false);

  // Manhole Schedule configure modal
  const LADDER_SERVICE_OPTIONS = ['Supply and Fix', 'Fix only'] as const;
  type MhLadderOption = 'N/A' | 'Stainless' | 'Galvanised';
  const [mhConfigModalVisible, setMhConfigModalVisible] = useState(false);
  const [mhConfigDraft, setMhConfigDraft] = useState<{ ladders: MhLadderOption; safetyChains: MhLadderOption }>({
    ladders: foulSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised',
    safetyChains: foulSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised',
  });

  useEffect(() => {
    const cfg = foulSewersData.manholes.config.manholeScheduleConfig;
    if (cfg) {
      setMhConfigDraft({ ladders: cfg.ladders, safetyChains: cfg.safetyChains });
    }
  }, [foulSewersData.manholes.config.manholeScheduleConfig]);

  const openMhConfigModal = () => {
    const cfg = foulSewersData.manholes.config.manholeScheduleConfig;
    setMhConfigDraft({
      ladders: cfg?.ladders ?? 'Galvanised',
      safetyChains: cfg?.safetyChains ?? 'Galvanised',
    });
    setMhConfigModalVisible(true);
  };

  const applyMhConfig = () => {
    const updated = (foulSewersData.manholes.table || []).map((mh) =>
      calcFoulManholeDerived(
        { ...mh, laddersType: mhConfigDraft.ladders, safetyChainType: mhConfigDraft.safetyChains },
        mhConfigDraft.ladders,
        mhConfigDraft.safetyChains,
      )
    );
    updateField('foulSewers', {
      ...foulSewersData,
      manholes: {
        ...foulSewersData.manholes,
        table: updated,
        config: { ...foulSewersData.manholes.config, manholeScheduleConfig: { ladders: mhConfigDraft.ladders, safetyChains: mhConfigDraft.safetyChains } },
      },
    });
    setMhConfigModalVisible(false);
  };

  // Road Construction Depth configure modal — Depth is Manual/Auto, same
  // convention as WallsSectionBuildUp.tsx's "Net" field: no separate flag,
  // "manual" is detected by comparing the stored depth against the
  // roadLevel − formationLevel auto-calculation.
  const [roadConstModalVisible, setRoadConstModalVisible] = useState(false);
  const [roadConstDraft, setRoadConstDraft] = useState<{ roadLevel: number; formationLevel: number; depth: number }>({ roadLevel: 0, formationLevel: 0, depth: 0 });
  const ROAD_CONST_DEPTH_EPSILON = 0.0005;
  const roadConstAutoDepth = (roadLevel: number, formationLevel: number) => parseFloat((roadLevel - formationLevel).toFixed(3));
  const roadConstDepthOverridden = Math.abs(roadConstDraft.depth - roadConstAutoDepth(roadConstDraft.roadLevel, roadConstDraft.formationLevel)) > ROAD_CONST_DEPTH_EPSILON;
  const getEffectiveRoadConstDepth = (cfg: FoulRoadConstConfig | undefined): number =>
    cfg ? (cfg.depth ?? roadConstAutoDepth(cfg.roadLevel, cfg.formationLevel)) : 0;

  const openRoadConstModal = () => {
    const saved = foulSewersData.pipeRuns.config.roadConstConfig;
    const roadLevel = saved?.roadLevel ?? 0;
    const formationLevel = saved?.formationLevel ?? 0;
    setRoadConstDraft({ roadLevel, formationLevel, depth: getEffectiveRoadConstDepth(saved) });
    setRoadConstModalVisible(true);
  };

  const updateRoadConstLevel = (field: 'roadLevel' | 'formationLevel', value: number) => {
    setRoadConstDraft((prev) => {
      const wasOverridden = Math.abs(prev.depth - roadConstAutoDepth(prev.roadLevel, prev.formationLevel)) > ROAD_CONST_DEPTH_EPSILON;
      const next = { ...prev, [field]: value };
      if (!wasOverridden) next.depth = roadConstAutoDepth(next.roadLevel, next.formationLevel);
      return next;
    });
  };

  const clearRoadConstDepthOverride = () =>
    setRoadConstDraft((prev) => ({ ...prev, depth: roadConstAutoDepth(prev.roadLevel, prev.formationLevel) }));

  const applyRoadConstConfig = () => {
    const depth    = roadConstDraft.depth;
    const manholes = foulSewersData.manholes.table || [];
    const updatedPipeRuns = foulSewersData.pipeRuns.table.map((run) =>
      calcPipeRunDerived({ ...run, roadConstDepth: depth }, manholes)
    );
    const updatedLaterals = (foulSewersData.lateralConnections.table || []).map((lat, i) =>
      calcLateralDerived(lat, manholes, updatedPipeRuns[i]?.runDepthCategory ?? 0)
    );
    updateField('foulSewers', {
      ...foulSewersData,
      pipeRuns: {
        ...foulSewersData.pipeRuns,
        table: updatedPipeRuns,
        config: {
          ...foulSewersData.pipeRuns.config,
          roadConstConfig: { roadLevel: roadConstDraft.roadLevel, formationLevel: roadConstDraft.formationLevel, depth: roadConstDraft.depth },
        },
      },
      lateralConnections: { ...foulSewersData.lateralConnections, table: updatedLaterals },
    });
    setRoadConstModalVisible(false);
  };

  // Pump Chamber configure modal
  const [pumpChamberModalVisible, setPumpChamberModalVisible] = useState(false);
  const [pumpChamberDraft, setPumpChamberDraft] = useState<PumpChamberConfig>({
    coverLevel: 0,
    invert: 0,
    sump: 0,
    chamberDia: 0,
    valveChamberWidth: 0,
    valveChamberLength: 0,
    valveChamberDepth: 0,
    motType1SubBaseMm: 0,
    pav2SlabMm: 0,
    meshType: '',
    pumpChamberMeshType: '',
    valveChamberMeshType: '',
    flowMeterChamberMeshType: '',
    kioskBaseMeshType: '',
    pumpChamberBlindingMm: 0,
    valveChamberConcreteGrade: '',
    flowMeterChamberConcreteGrade: '',
    kioskBaseConcreteGrade: '',
    valveChamberLaddersService: '',
    flowMeterChamberLaddersService: '',
  });

  const openPumpChamberModal = () => {
    const saved = state.pumpStation.config as PumpChamberConfig | Record<string, never>;
    setPumpChamberDraft({
      coverLevel: (saved as PumpChamberConfig)?.coverLevel ?? 0,
      invert: (saved as PumpChamberConfig)?.invert ?? 0,
      sump: (saved as PumpChamberConfig)?.sump ?? 0,
      chamberDia: (saved as PumpChamberConfig)?.chamberDia ?? 0,
      valveChamberWidth: (saved as PumpChamberConfig)?.valveChamberWidth ?? 0,
      valveChamberLength: (saved as PumpChamberConfig)?.valveChamberLength ?? 0,
      valveChamberDepth: (saved as PumpChamberConfig)?.valveChamberDepth ?? 0,
      motType1SubBaseMm: (saved as PumpChamberConfig)?.motType1SubBaseMm ?? 0,
      pav2SlabMm: (saved as PumpChamberConfig)?.pav2SlabMm ?? 0,
      meshType: (saved as PumpChamberConfig)?.meshType ?? '',
      pumpChamberMeshType: (saved as PumpChamberConfig)?.pumpChamberMeshType ?? '',
      valveChamberMeshType: (saved as PumpChamberConfig)?.valveChamberMeshType ?? '',
      flowMeterChamberMeshType: (saved as PumpChamberConfig)?.flowMeterChamberMeshType ?? '',
      kioskBaseMeshType: (saved as PumpChamberConfig)?.kioskBaseMeshType ?? '',
      pumpChamberBlindingMm: (saved as PumpChamberConfig)?.pumpChamberBlindingMm ?? 0,
      valveChamberConcreteGrade: (saved as PumpChamberConfig)?.valveChamberConcreteGrade ?? '',
      flowMeterChamberConcreteGrade: (saved as PumpChamberConfig)?.flowMeterChamberConcreteGrade ?? '',
      kioskBaseConcreteGrade: (saved as PumpChamberConfig)?.kioskBaseConcreteGrade ?? '',
      valveChamberLaddersService: (saved as PumpChamberConfig)?.valveChamberLaddersService ?? '',
      flowMeterChamberLaddersService: (saved as PumpChamberConfig)?.flowMeterChamberLaddersService ?? '',
    });
    setPumpChamberModalVisible(true);
  };

  const applyPumpChamberConfig = () => {
    updateField('pumpStation', {
      ...state.pumpStation,
      config: { ...pumpChamberDraft, sectionTitles: pumpStationSectionTitles },
    });
    setPumpChamberModalVisible(false);
  };

  // Pump Station section titles (header rows): the user appends text after the default
  // title (never rewrites it) — stored in pumpStation.config.sectionTitles; an empty save removes it.
  const pumpStationSectionTitles = (state.pumpStation.config as PumpChamberConfig | undefined)?.sectionTitles;
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const saveSectionTitle = (id: string) => {
    const next = { ...(pumpStationSectionTitles ?? {}) };
    const trimmed = titleDraft.trim();
    if (trimmed) next[id] = trimmed;
    else delete next[id];
    updateField('pumpStation', {
      ...state.pumpStation,
      config: { ...(state.pumpStation.config as PumpChamberConfig), sectionTitles: next },
    });
    setEditingTitleId(null);
  };

  // const pumpChamberDepthCalc = parseFloat((pumpChamberDraft.coverLevel - pumpChamberDraft.invert + pumpChamberDraft.sump).toFixed(3));
  // const pumpChamberDepthCategoryCalc = Math.round(pumpChamberDepthCalc / 0.25) * 0.25;

  // Saved (applied) Pump Chamber config — drives the "Wet Well" build-up row label, independent of the draft/modal.
  const savedPumpChamberConfig = (state.pumpStation.config as PumpChamberConfig)?.coverLevel !== undefined
    ? (state.pumpStation.config as PumpChamberConfig)
    : undefined;
  const savedPumpChamberDepth = savedPumpChamberConfig
    ? parseFloat((savedPumpChamberConfig.coverLevel - savedPumpChamberConfig.invert + savedPumpChamberConfig.sump).toFixed(3))
    : 0;
  const savedPumpChamberDepthCategory = Math.round(savedPumpChamberDepth / 0.25) * 0.25;
  const wetWellDescription = `Wet Well ${savedPumpChamberConfig?.chamberDia ?? 0}mmØ ${savedPumpChamberDepthCategory.toFixed(3)}m deep`;
  const motType1SubBaseDescription = savedPumpChamberConfig?.motType1SubBaseMm
    ? `${savedPumpChamberConfig.motType1SubBaseMm}mm MOT type 1 Sub Base`
    : 'xxxmm MOT type 1 Sub Base';
  const pav2SlabDescription = savedPumpChamberConfig?.pav2SlabMm
    ? `${savedPumpChamberConfig.pav2SlabMm}mm thick PAV2 concrete slab`
    : 'xxxmm thick PAV2 concrete slab';
  const ps10MeshDescription = savedPumpChamberConfig?.meshType
    ? `${savedPumpChamberConfig.meshType} mesh x 2 layers to concrete base only`
    : 'xxxx mesh x 2 layers to concrete base only';
  const pumpChamberMeshDescription = savedPumpChamberConfig?.pumpChamberMeshType
    ? `2 Layers ${savedPumpChamberConfig.pumpChamberMeshType} Mesh to Base`
    : '2 Layers xxxx Mesh to Base';
  const valveChamberMeshDescription = savedPumpChamberConfig?.valveChamberMeshType
    ? `${savedPumpChamberConfig.valveChamberMeshType} mesh to concrete base only`
    : 'xxxx mesh to concrete base only';
  const flowMeterChamberMeshDescription = savedPumpChamberConfig?.flowMeterChamberMeshType
    ? `${savedPumpChamberConfig.flowMeterChamberMeshType} mesh to concrete base only`
    : 'xxxx mesh to concrete base only';
  const kioskBaseMeshDescription = savedPumpChamberConfig?.kioskBaseMeshType
    ? `${savedPumpChamberConfig.kioskBaseMeshType} mesh`
    : 'xxxx mesh';
  const pumpChamberBlindingDescription = savedPumpChamberConfig?.pumpChamberBlindingMm
    ? `${savedPumpChamberConfig.pumpChamberBlindingMm}mm thick concrete blinding`
    : 'xxmm thick concrete blinding';
  const valveChamberConcreteDescription = savedPumpChamberConfig?.valveChamberConcreteGrade
    ? `${savedPumpChamberConfig.valveChamberConcreteGrade} Concrete in Base`
    : 'xxxxx Concrete in Base';
  const flowMeterChamberConcreteDescription = savedPumpChamberConfig?.flowMeterChamberConcreteGrade
    ? `${savedPumpChamberConfig.flowMeterChamberConcreteGrade} Concrete in Base`
    : 'xxxxx Concrete in Base';
  const kioskBaseConcreteDescription = savedPumpChamberConfig?.kioskBaseConcreteGrade
    ? `${savedPumpChamberConfig.kioskBaseConcreteGrade} Concrete Base`
    : 'xxxxx Concrete Base';
  const valveChamberHeaderText = `VALVE CHAMBER = ${(savedPumpChamberConfig?.valveChamberWidth ?? 0).toFixed(3)}M WIDE X ${(savedPumpChamberConfig?.valveChamberLength ?? 0).toFixed(3)}M LONG X ${(savedPumpChamberConfig?.valveChamberDepth ?? 0).toFixed(3)}M DEEP INTERNAL DIMENSIONS`;

  // Ex Topsoil Depth Utilised (Global Settings, metres) drives the "reduce
  // levels to take up Xmm top soil" description and qty on the Pump Station
  // build-up row 'ps_2' — qty = Ex Topsoil Depth Utilised × 'ps_1' (Site strip) qty.
  const exTopsoilDepthUtilised = state.roads.cutFillSections.config.stripValueDefault ?? 0.3;
  const topsoilStripDescription = `Excavate to reduce levels to take up ${Math.round(exTopsoilDepthUtilised * 1000)}mm top soil, deposit onsite`;

  // Excel import/export states
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const eoConcBedQtys = foulSewersData.pipeRuns.config.eoQtys || {};
  const setEoConcBedQty = (key: string, val: number) =>
    updateField('foulSewers', {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, config: { ...foulSewersData.pipeRuns.config, eoQtys: { ...eoConcBedQtys, [key]: val } } },
    });

  const [summaryExpandedKeys, setSummaryExpandedKeys] = useState<React.Key[]>([]);
  const [formulaInfoKey, setFormulaInfoKey] = useState<string | null>(null);
  const [surplusBackfillFormula, setSurplusBackfillFormula] = useState<FoulFormulaInfo | null>(null);
  const [summaryView, setSummaryView] = useState<'summary' | 'surplus' | 'backfill'>('summary');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSummaryExpandedKeys([]);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  interface ColCalcInfo {
    title: string;
    formula: string;
    rows: { ref: string; value: number | string; unit: string }[];
    unit: string;
  }
  const [colCalcInfo, setColCalcInfo] = useState<ColCalcInfo | null>(null);

  // Get all manholes for foul sewers
  const availableManholes = foulSewersData.manholes.table || [];

  // Auto-calculate derived fields when manholes or pipe runs change
  useEffect(() => {
    const calculateOffset = () => {
      const baseOffset = 144;
      const stickyHeader = document.getElementsByClassName('foul-sewers-sticky-header')?.[0];
      const height = stickyHeader?.getBoundingClientRect?.()?.height || 0;
      setStickyOffset(baseOffset + height);
    };
    calculateOffset();
    const timer = setTimeout(calculateOffset, 100);
    return () => clearTimeout(timer);
    // selectedManholeKeys/selectedCombinedKeys are included because the "Delete
    // Selected" button appearing/disappearing next to Configure can change
    // whether the tab row wraps onto a second line, changing its height.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedManholeKeys.length, selectedCombinedKeys.length]);


  const addManhole = () => {
    const newManhole: Manhole = {
      id: Date.now().toString(),
      ref: "",
      type: "",
      easiTrad: undefined,
      diameter: "",
      coverLevel: 0,
      invertLevel: 0,
      depth: 0,
      depthCategory: 0,
      ladders: 0,
      safetyChains: 0,
      backdrop: 0,
      muckStopper: 0,
      amps: 0,
    };

    updateField("foulSewers", {
      ...foulSewersData,
      manholes: { ...foulSewersData.manholes, table: [...(foulSewersData.manholes.table || []), newManhole] },
    });
  };

  // Export manholes to CSV
  const exportManholes = () => {
    const manholes = foulSewersData.manholes.table || [];
    if (manholes.length === 0) return;

    const headers = [
      "MH Ref",
      "Cover Level (m)",
      "Invert Level (m)",
      "Depth (m)",
      "Depth Category",
      "Diameter",
    ];
    const csvData = [
      headers,
      ...manholes.map((manhole) => [
        manhole.ref,
        manhole.coverLevel.toString(),
        manhole.invertLevel.toString(),
        manhole.depth.toString(),
        manhole.depthCategory.toString(),
        manhole.diameter,
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `foul_manholes_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import manholes from CSV
  const importManholes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const lines = csv.split("\n");
        const headers = lines[0].split(",");

        // Validate headers (trim whitespace and case-insensitive)
        const expectedHeaders = [
          "MH Ref",
          "Cover Level (m)",
          "Invert Level (m)",
          "Depth (m)",
          "Depth Category",
          "Diameter",
        ];
        const trimmedHeaders = headers.map((h) => h.trim());
        const isValidFormat = expectedHeaders.every((header) =>
          trimmedHeaders.some((h) => h.toLowerCase() === header.toLowerCase())
        );

        if (!isValidFormat) {
          console.error("Invalid CSV format. Expected headers:", expectedHeaders.join(", "));
          return;
        }

        // Find header positions
        const getHeaderIndex = (headerName: string) => {
          return trimmedHeaders.findIndex(
            (h) => h.toLowerCase() === headerName.toLowerCase()
          );
        };

        const refIndex = getHeaderIndex("MH Ref");
        const coverLevelIndex = getHeaderIndex("Cover Level (m)");
        const invertLevelIndex = getHeaderIndex("Invert Level (m)");
        const diameterIndex = getHeaderIndex("Diameter");

        const importedManholes: Manhole[] = [];

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const values = line.split(",").map((v) => v.trim());
          if (values.length < expectedHeaders.length) continue;

          const manhole: Manhole = {
            id: Date.now().toString() + "_" + i,
            ref: values[refIndex] || `MH${i}`,
            coverLevel: parseFloat(values[coverLevelIndex]) || 0,
            invertLevel: parseFloat(values[invertLevelIndex]) || 0,
            depth: 0, // Will be calculated below
            depthCategory: 0, // Will be calculated below
            diameter: values[diameterIndex] || "1200mm",
            type: "foul",
          };

          // Recalculate all derived fields
          const derived = calcFoulManholeDerived(
            manhole,
            foulSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised',
            foulSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised',
          );
          importedManholes.push(derived);
        }

        if (importedManholes.length > 0) {
          // Update manholes: if MH Ref exists, update it; otherwise add new
          const existingManholes = foulSewersData.manholes.table || [];
          const updatedManholes = [...existingManholes];
          let updatedCount = 0;
          let addedCount = 0;

          importedManholes.forEach((importedManhole) => {
            const existingIndex = updatedManholes.findIndex(
              (mh) => mh.ref === importedManhole.ref
            );
            if (existingIndex >= 0) {
              // Update existing manhole
              updatedManholes[existingIndex] = {
                ...updatedManholes[existingIndex],
                ...importedManhole,
              };
              updatedCount++;
            } else {
              // Add new manhole
              updatedManholes.push(importedManhole);
              addedCount++;
            }
          });

          updateField("foulSewers", {
            ...foulSewersData,
            manholes: { ...foulSewersData.manholes, table: updatedManholes },
          });
        } else {
          console.warn("No valid manholes found in the CSV file");
        }
      } catch (error) {
        console.error("Error reading CSV file:", error);
      }
    };
    reader.readAsText(file);

    // Reset the input value so the same file can be selected again
    event.target.value = "";
  };

  // Main Sewer Runs and House Laterals are displayed as paired columns in the
  // same take-off row (see combinedFoulTakeOffData below), but they're stored
  // as two independent arrays — imports in particular fill them from separate
  // row sets, so a run with no lateral (or vice versa) is normal data, not an
  // error. These helpers keep the two arrays the same length by padding the
  // shorter one with blank-but-editable entries, so every row always has a
  // real object on both sides instead of rendering a non-editable "—".
  const makeBlankPipeRun = (roadConstDepth: number, seed: number): SewerPipeRun => ({
    id: `pr-${seed}`,
    ref: '', fromMH: '', toMH: '', length: 0, diameter: '',
    depthFrom: 0, depthTo: 0, averageDepth: 0, depthCategory: 0,
    runDepthCategory: 0, rockers: 0, pipeType: '', backfillType: '',
    roadConstDepth, concreteProtection: 0, junctionSize: '', junctions: 0,
  });

  const makeBlankLateral = (seed: number): LateralConnection => ({
    id: `lat-${seed}`,
    ref: '', connectionType: 'direct_to_manhole',
    length: 0, pipeDiameter: '', pipeType: '', manholeDiameter: '',
    backdrop: 0, depth: 0, requiresFirstOff: false, rockers: 0,
    junctionOffRun: 0, demarcCoverLevel: 0, demarcInvertLevel: 0,
    demarcDepth: 0, demarcDepthCategory: 0, nme: 0, count1200: 0,
  });

  const padPipeRuns = (runs: SewerPipeRun[], targetLength: number, roadConstDepth: number, seedBase: number): SewerPipeRun[] =>
    runs.length >= targetLength
      ? runs
      : [...runs, ...Array.from({ length: targetLength - runs.length }, (_, i) => makeBlankPipeRun(roadConstDepth, seedBase + i))];

  const padLaterals = (laterals: LateralConnection[], targetLength: number, seedBase: number): LateralConnection[] =>
    laterals.length >= targetLength
      ? laterals
      : [...laterals, ...Array.from({ length: targetLength - laterals.length }, (_, i) => makeBlankLateral(seedBase + i))];

  // Unified Excel Export
  const handleUnifiedExport = async () => {
    try {
      const data: FoulSewersExcelData = {
        manholes: availableManholes,
        pipeRuns: foulSewersData.pipeRuns.table,
        laterals: foulSewersData.lateralConnections.table || [],
      };

      await exportFoulSewersToExcel(data, state.projectName || "FoulSewers");
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Generate Template
  const handleDownloadTemplate = async () => {
    try {
      await generateFoulSewersTemplate(state.projectName || "FoulSewers");
    } catch (error) {
      console.error("Template generation failed:", error);
    }
  };

  // Unified Excel Import
  const handleUnifiedImport = async (file: File) => {
    setImportLoading(true);
    try {
      const result = await importFoulSewersFromExcel(file);

      // The sheet's main-run and house-lateral sections are read independently
      // (not every run has a lateral), so the imported arrays commonly end up
      // different lengths. Pad the shorter one so every take-off row renders
      // with editable inputs on both sides straight after import.
      const importedRowCount = Math.max(result.data.pipeRuns.length, result.data.laterals.length);
      const importRoadConstDepth = result.data.roadConstConfig
        ? parseFloat((result.data.roadConstConfig.roadLevel - result.data.roadConstConfig.formationLevel).toFixed(3))
        : 0;
      const importSeed = Date.now();

      // Completely replace all imported data — do not merge with existing records.
      // eoQtys is the exception: it holds the Foul Summary tab's manual-input-only
      // quantities (EO Concrete Bed & Surround, EO Items, Removal of Redundant
      // Sewer), matched by description text out of the 'Foul Summary' sheet if
      // present (see parseFoulSummarySheet) — merge rather than replace so a
      // workbook without that sheet, or with only some of the 17 rows filled in,
      // doesn't wipe out previously-entered values.
      // The imported build-up rows are keyed 'ps_*' (Pump Station) / 'rm_*'
      // (Rising Main) — split them by prefix so each lands in its own
      // top-level state field (state.pumpStation.table / state.risingMain.table).
      const importedBuildUp = result.data.buildUp || {};
      const importedPumpStationRows: Record<string, FoulBuildUpRowData> = {};
      const importedRisingMainRows: Record<string, FoulBuildUpRowData> = {};
      // evaluateCode is intentionally dropped here — codes are now sourced live
      // from foulBuildUpEvaluateCodesMapping.ts (see getBuildUpEvaluateCode
      // below), not from the sheet, so only quant is kept in saved/API state.
      Object.entries(importedBuildUp).forEach(([id, row]) => {
        if (id.startsWith('ps_')) importedPumpStationRows[id] = { quant: row.quant, evaluateCode: '' };
        else if (id.startsWith('rm_')) importedRisingMainRows[id] = { quant: row.quant, evaluateCode: '' };
      });

      const updatedFoulSewers = {
        ...foulSewersData,
        manholes: {
          ...foulSewersData.manholes,
          table: result.data.manholes,
          config: {
            ...foulSewersData.manholes.config,
            ...(result.data.manholeScheduleConfig ? { manholeScheduleConfig: result.data.manholeScheduleConfig } : {}),
          },
        },
        pipeRuns: {
          ...foulSewersData.pipeRuns,
          table: padPipeRuns(result.data.pipeRuns, importedRowCount, importRoadConstDepth, importSeed),
          config: {
            ...foulSewersData.pipeRuns.config,
            ...(result.data.roadConstConfig ? { roadConstConfig: result.data.roadConstConfig } : {}),
            ...(result.data.eoQtys ? { eoQtys: { ...foulSewersData.pipeRuns.config.eoQtys, ...result.data.eoQtys } } : {}),
          },
        },
        lateralConnections: {
          ...foulSewersData.lateralConnections,
          table: padLaterals(result.data.laterals, importedRowCount, importSeed + importedRowCount),
        },
      };

      updateField("foulSewers", updatedFoulSewers);
      updateField("pumpStation", {
        ...state.pumpStation,
        table: importedPumpStationRows,
        // Merge rather than replace — the imported sheet only carries Pump
        // Chamber's Cover Level/Invert/Sump/Chamber Dia, not Valve Chamber's
        // Width/Length/Depth (Configure-modal-only), so a full replace would
        // silently wipe out an already-configured Valve Chamber.
        ...(result.data.pumpChamberConfig || result.data.pumpStationSectionTitles ? {
          config: {
            ...(state.pumpStation.config as PumpChamberConfig),
            ...(result.data.pumpChamberConfig ?? {}),
            // Titles come from the sheet, replacing any previously typed ones (same as the imported table).
            ...(result.data.pumpStationSectionTitles ? { sectionTitles: result.data.pumpStationSectionTitles } : {}),
          },
        } : {}),
      });
      updateField("risingMain", {
        ...state.risingMain,
        table: importedRisingMainRows,
      });
      setImportModalVisible(false);
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setImportLoading(false);
    }
  };

  const addPipeRun = () => {
    const cfg = foulSewersData.pipeRuns.config.roadConstConfig;
    const roadConstDepth = getEffectiveRoadConstDepth(cfg);
    const newPipeRun: SewerPipeRun = {
      id: Date.now().toString(),
      ref: "",
      fromMH: "",
      toMH: "",
      length: 0,
      diameter: "",
      depthFrom: 0,
      depthTo: 0,
      averageDepth: 0,
      depthCategory: 0,
      runDepthCategory: 0,
      rockers: 0,
      pipeType: "",
      backfillType: "",
      roadConstDepth,
      concreteProtection: 0,
      junctionSize: "",
      junctions: 0,
    };

    updateField("foulSewers", {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, table: [...foulSewersData.pipeRuns.table, newPipeRun] },
    });
  };

  const updateManhole = (id: string, field: keyof Manhole, value: unknown) => {
    const ladderCfg = foulSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised';
    const safetyCfg = foulSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised';
    const oldRef = (foulSewersData.manholes.table || []).find((mh) => mh.id === id)?.ref;
    const updatedManholes = (foulSewersData.manholes.table || []).map((mh) => {
      if (mh.id !== id) return mh;
      return calcFoulManholeDerived({ ...mh, [field]: value }, ladderCfg, safetyCfg);
    });

    // Renaming a manhole's ref invalidates any Manhole From/To or lateral
    // "Manhole Ref" selection pointing at the old ref — clear those instead of
    // leaving them showing a ref that no longer exists on any manhole.
    let updatedPipeRuns = foulSewersData.pipeRuns.table;
    let updatedLaterals = foulSewersData.lateralConnections.table;
    if (field === 'ref' && oldRef && value !== oldRef) {
      updatedPipeRuns = updatedPipeRuns.map((run) => {
        if (run.fromMH !== oldRef && run.toMH !== oldRef) return run;
        return calcPipeRunDerived({
          ...run,
          fromMH: run.fromMH === oldRef ? '' : run.fromMH,
          toMH: run.toMH === oldRef ? '' : run.toMH,
        }, updatedManholes);
      });
      updatedLaterals = (updatedLaterals || []).map((lat, i) => {
        if (lat.toManhole !== oldRef) return lat;
        return calcLateralDerived({ ...lat, toManhole: '' }, updatedManholes, updatedPipeRuns[i]?.runDepthCategory ?? 0);
      });
    }

    updateField("foulSewers", {
      ...foulSewersData,
      manholes: { ...foulSewersData.manholes, table: updatedManholes },
      pipeRuns: { ...foulSewersData.pipeRuns, table: updatedPipeRuns },
      lateralConnections: { ...foulSewersData.lateralConnections, table: updatedLaterals },
    });
  };

  const updatePipeRun = (id: string, field: keyof SewerPipeRun, value: unknown) => {
    const manholes  = foulSewersData.manholes.table || [];
    const runIndex  = foulSewersData.pipeRuns.table.findIndex(r => r.id === id);
    const updatedPipeRuns = foulSewersData.pipeRuns.table.map((run, i) => {
      if (i !== runIndex) return run;
      return calcPipeRunDerived({ ...run, [field]: value }, manholes);
    });
    const updatedLaterals = (foulSewersData.lateralConnections.table || []).map((lat, i) => {
      if (i !== runIndex) return lat;
      return calcLateralDerived(lat, manholes, updatedPipeRuns[runIndex]?.runDepthCategory ?? 0);
    });
    updateField("foulSewers", {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, table: updatedPipeRuns },
      lateralConnections: { ...foulSewersData.lateralConnections, table: updatedLaterals },
    });
  };

  const updateLaterals = (laterals: LateralConnection[]) => {
    updateField("foulSewers", {
      ...foulSewersData,
      lateralConnections: { ...foulSewersData.lateralConnections, table: laterals },
    });
  };

  const updateLateral = (id: string, field: keyof LateralConnection, value: unknown) => {
    const manholes  = foulSewersData.manholes.table || [];
    const laterals  = foulSewersData.lateralConnections.table || [];
    const latIndex  = laterals.findIndex(l => l.id === id);
    const pairedRunDepthCategory = foulSewersData.pipeRuns.table[latIndex]?.runDepthCategory ?? 0;
    const updated = laterals.map((l, i) => {
      if (i !== latIndex) return l;
      return calcLateralDerived({ ...l, [field]: value }, manholes, pairedRunDepthCategory);
    });
    updateLaterals(updated);
  };

  const deleteManhole = (id: string) => {
    const allManholes = foulSewersData.manholes.table || [];
    const updatedManholes = allManholes.filter((mh) => mh.id !== id);

    // Remove pipe runs that reference this manhole
    const deletedMH = allManholes.find((mh) => mh.id === id);
    const updatedFoulPipeRuns = foulSewersData.pipeRuns.table.filter((run) => {
      return deletedMH
        ? run.fromMH !== deletedMH.ref && run.toMH !== deletedMH.ref
        : true;
    });

    // Update foul sewers with updated manholes and pipe runs
    updateField("foulSewers", {
      ...foulSewersData,
      manholes: { ...foulSewersData.manholes, table: updatedManholes },
      pipeRuns: { ...foulSewersData.pipeRuns, table: updatedFoulPipeRuns },
    });

    // Force immediate save to ensure deletion persists
    setTimeout(() => {
      saveTender();
      console.log("Manhole deletion saved to storage");
    }, 100);
  };

  const deleteManholes = (ids: React.Key[]) => {
    const idSet = new Set(ids);
    const allManholes = foulSewersData.manholes.table || [];
    const deletedRefs = new Set(allManholes.filter((mh) => idSet.has(mh.id)).map((mh) => mh.ref));
    const updatedManholes = allManholes.filter((mh) => !idSet.has(mh.id));
    const updatedFoulPipeRuns = foulSewersData.pipeRuns.table.filter(
      (run) => !deletedRefs.has(run.fromMH) && !deletedRefs.has(run.toMH)
    );

    updateField("foulSewers", {
      ...foulSewersData,
      manholes: { ...foulSewersData.manholes, table: updatedManholes },
      pipeRuns: { ...foulSewersData.pipeRuns, table: updatedFoulPipeRuns },
    });

    setTimeout(() => {
      saveTender();
    }, 100);
  };

  const deletePipeRun = (id: string) => {
    const updatedPipeRuns = foulSewersData.pipeRuns.table.filter(
      (run) => run.id !== id
    );

    updateField("foulSewers", {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, table: updatedPipeRuns },
    });

    // Force immediate save to ensure deletion persists
    setTimeout(() => {
      saveTender();
      console.log("Pipe run deletion saved to storage");
    }, 100);
  };

  const isEditing = (table: string, id: string, field: string) => {
    return (
      editingCell?.table === table &&
      editingCell?.id === id &&
      editingCell?.field === field
    );
  };

  const handleCellEdit = (
    table: string,
    id: string,
    field: string,
    value: any
  ) => {
    if (table === "manholes") {
      updateManhole(id, field as keyof Manhole, value);
    } else if (table === "pipeRuns") {
      updatePipeRun(id, field as keyof SewerPipeRun, value);
    }
  };

  const handleCellEditComplete = () => {
    setEditingCell(null);
  };

  const totalLength = calculateTotalPipeLength(foulSewersData.pipeRuns.table);

  // Calculate detailed summaries
  const pipeRunsSummary = calculateFoulSewersSummary(
    foulSewersData.pipeRuns.table,
    availableManholes
  );
  const manholesSummary = calculateManholesSummary(availableManholes);
  const manholeScheduleSummary =
    calculateManholeScheduleSummary(availableManholes);
  const pipeRunsScheduleSummary = calculatePipeRunsScheduleSummary(
    foulSewersData.pipeRuns.table
  );
  const lateralConnectionsSummary = calculateConnectionSummary(
    foulSewersData.lateralConnections.table || []
  );

  const calcFoulJunctionSize = (run: SewerPipeRun, lat: LateralConnection | null): string => {
    const mainDiamNum = parseInt((run.diameter ?? '').replace(/mm$/i, ''), 10) || 0;
    const latDiamNum = parseInt((lat?.pipeDiameter ?? '').replace(/mm$/i, ''), 10) || 0;
    const pipeType = (run.pipeType || '').toLowerCase();
    if (pipeType === 'rigi' && mainDiamNum >= 450) return `${mainDiamNum}mm Saddle`;
    if ((pipeType === 'conc' || pipeType === 'concrete') && mainDiamNum >= 525) return 'Core Drill';
    if (latDiamNum > 0 && mainDiamNum > 0) return `${mainDiamNum} x ${latDiamNum}mm junction`;
    return '';
  };

  const calcCellStyle: React.CSSProperties = {
    padding: '4px 8px', textAlign: 'center', background: '#f5f5f5',
    borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13,
  };

  const colHeader = (title: string, unit?: string): React.ReactNode => (
    <div className="text-center">
      <Tooltip title={title}>
        <div className="gw-header-title">{title}</div>
      </Tooltip>
      {unit && <div className="gw-header-unit text-xs text-gray-500">{unit}</div>}
    </div>
  );

  const colHeaderCalc = (
    title: string,
    unit: string | undefined,
    info: { formula: string; excelRef?: string; rows: { ref: string; value: number | string; unit: string }[] },
  ): React.ReactNode => (
    <div className="text-center">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <div className="gw-header-title">{title}</div>
        {showDevMode && (
          <InfoCircleOutlined
            style={{ color: '#1677ff', cursor: 'pointer', flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setColCalcInfo({ title, formula: info.formula, rows: info.rows, unit: unit ?? '' });
            }}
          />
        )}
      </div>
      {unit && <div className="gw-header-unit text-xs text-gray-500">{unit}</div>}
    </div>
  );

  const manholeScheduleContent = (
    <>
      <Table
        className="foul-sewers-table"
        bordered
        size="small"
        scroll={{ x: 1545 }}
        sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
        pagination={false}
        rowSelection={
          state.isViewMode
            ? undefined
            : {
                selectedRowKeys: selectedManholeKeys,
                onChange: setSelectedManholeKeys,
                fixed: true,
              }
        }
        columns={[
          {
            title: colHeader('Manhole Ref'),
            dataIndex: 'ref',
            key: 'ref',
            width: 100,
            fixed: 'left' as const,
            render: (_, record) => (
              <Input
                size="small"
                value={record.ref}
                onChange={(e) => handleCellEdit('manholes', record.id, 'ref', e.target.value)}
              />
            ),
          },
          {
            title: colHeader('Type'),
            dataIndex: 'type',
            key: 'type',
            width: 90,
            render: (_, record) => (
              <Input
                size="small"
                value={record.type}
                onChange={(e) => handleCellEdit('manholes', record.id, 'type', e.target.value)}
              />
            ),
          },
          {
            title: colHeaderCalc('Easi / Trad', undefined, {
              formula: 'IF(Diam="","", IF(Diam ∈ {1200,1500,1800}, "Easi", "Trad"))',
              excelRef: 'D9',
              rows: availableManholes.map((mh) => ({
                ref: mh.ref || mh.id,
                value: mh.easiTrad === 'easi' ? 'Easi' : mh.easiTrad === 'trad' ? 'Trad' : '',
                unit: '',
              })),
            }),
            dataIndex: 'easiTrad',
            key: 'easiTrad',
            width: 130,
            align: 'center' as const,
            render: (_, record) => (
              <div style={{ padding: '4px 8px', textAlign: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13 }}>
                {record.easiTrad === 'easi' ? 'Easi' : record.easiTrad === 'trad' ? 'Trad' : <span style={{ color: '#d1d5db' }}>—</span>}
              </div>
            ),
          },
          {
            title: colHeader('Diam', 'mm'),
            dataIndex: 'diameter',
            key: 'diameter',
            width: 100,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={parseInt((record.diameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updateManhole(record.id, 'diameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Cover Level', 'm'),
            dataIndex: 'coverLevel',
            key: 'coverLevel',
            width: 110,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.coverLevel}
                onChange={(value) => updateManhole(record.id, 'coverLevel', value)}
                decimalPlaces={3}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Invert Level', 'm'),
            dataIndex: 'invertLevel',
            key: 'invertLevel',
            width: 120,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.invertLevel}
                onChange={(value) => updateManhole(record.id, 'invertLevel', value)}
                decimalPlaces={3}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeaderCalc('Depth To Invert', 'm', {
              formula: 'Cover Level − Invert Level',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.depth ?? 0, unit: 'm' })),
            }),
            dataIndex: 'depth',
            key: 'depth',
            width: 160,
            align: 'center' as const,
            render: (_, record) => (
              <div style={{ padding: '4px 8px', textAlign: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13 }}>
                {(record.depth ?? 0).toFixed(3)}
              </div>
            ),
          },
          {
            title: colHeaderCalc('Depth Category', 'm', {
              formula: 'ROUND(Depth To Invert / 0.25) × 0.25  →  nearest 0.25m band',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.depthCategory ?? 0, unit: 'm' })),
            }),
            dataIndex: 'depthCategory',
            key: 'depthCategory',
            width: 150,
            align: 'center' as const,
            render: (_, record) => (
              <div style={{ padding: '4px 8px', textAlign: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13 }}>
                {(record.depthCategory ?? 0).toFixed(3)}
              </div>
            ),
          },
          {
            title: colHeaderCalc('Ladders', 'm', {
              formula: 'N/A in configure → blank  |  Depth Category ≤ 2.25 → blank  |  else: Cover Level − Invert Level − 0.15',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.ladders ?? 0, unit: 'm' })),
            }),
            dataIndex: 'ladders',
            key: 'ladders',
            width: 100,
            align: 'center' as const,
            render: (_, record) => {
              const v = record.ladders ?? 0;
              return <div style={{ padding: '4px 8px', textAlign: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13 }}>{v > 0 ? v.toFixed(3) : '—'}</div>;
            },
          },
          {
            title: colHeaderCalc('Safety Chains', 'Nr', {
              formula: 'N/A in configure → blank  |  Depth Category ≤ 2.25 → blank  |  else: Cover Level − Invert Level − 0.15',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.safetyChains ?? 0, unit: 'Nr' })),
            }),
            dataIndex: 'safetyChains',
            key: 'safetyChains',
            width: 145,
            align: 'center' as const,
            render: (_, record) => {
              const v = record.safetyChains ?? 0;
              return <div style={{ padding: '4px 8px', textAlign: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13 }}>{v > 0 ? v.toFixed(3) : '—'}</div>;
            },
          },
          {
            title: colHeader('Backdrop', 'Nr'),
            dataIndex: 'backdrop',
            key: 'backdrop',
            width: 90,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.backdrop ?? 0}
                onChange={(value) => updateManhole(record.id, 'backdrop', value)}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Muck stopper', 'Nr'),
            dataIndex: 'muckStopper',
            key: 'muckStopper',
            width: 120,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.muckStopper ?? 0}
                onChange={(value) => updateManhole(record.id, 'muckStopper', value)}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('AMPS', 'Nr'),
            dataIndex: 'amps',
            key: 'amps',
            width: 80,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.amps ?? 0}
                onChange={(value) => updateManhole(record.id, 'amps', value)}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: 'Actions',
            key: 'actions',
            width: 80,
            fixed: 'right' as const,
            align: 'center' as const,
            render: (_, record) => (
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => requestDelete(() => deleteManhole(record.id))}
              />
            ),
          },
        ]}
        dataSource={availableManholes}
        rowKey="id"
      />

      {/* Add Manhole Button */}
      <div style={{ marginTop: '16px' }}>
        <Button
          type="text"
          icon={<PlusOutlined style={{ color: COLORS.primary }} />}
          onClick={addManhole}
          style={{
            color: COLORS.primary,
            fontWeight: 500,
            padding: '4px 8px'
          }}
        >
          Add New Row
        </Button>
      </div>

    </>
  );

  // ── Combined Foul Take-off table (Main Sewer Runs + House Laterals) ──────────
  // Each row pairs pipeRuns[i] with lateralConnections[i] – same row, like RetainingWalls garden/drive columns.

  interface FoulTakeOffRow {
    id: string;
    index: number;
    pipeRun: SewerPipeRun | null;
    lateral: LateralConnection | null;
  }

  const _pipeRuns = foulSewersData.pipeRuns.table || [];
  const _laterals = foulSewersData.lateralConnections.table || [];
  const _numRows = Math.max(_pipeRuns.length, _laterals.length);

  const combinedFoulTakeOffData: FoulTakeOffRow[] = Array.from({ length: _numRows }, (_, i) => ({
    id: `ft-row-${i}`,
    index: i,
    pipeRun: _pipeRuns[i] ?? null,
    lateral: _laterals[i] ?? null,
  }));

  const addCombinedRow = () => {
    const cfg = foulSewersData.pipeRuns.config.roadConstConfig;
    const roadConstDepth = getEffectiveRoadConstDepth(cfg);
    const now = Date.now();

    // Pad both lists up to the longer one's length first, so the row appended
    // below always lands at the same index in both arrays and renders with
    // editable inputs (see padPipeRuns/padLaterals above).
    const runsInSync = padPipeRuns(_pipeRuns, _numRows, roadConstDepth, now);
    const lateralsInSync = padLaterals(_laterals, _numRows, now + _numRows);

    const newRun: SewerPipeRun = makeBlankPipeRun(roadConstDepth, now + runsInSync.length);
    const newLateral: LateralConnection = makeBlankLateral(now + lateralsInSync.length + 1);

    updateField('foulSewers', {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, table: [...runsInSync, newRun] },
      lateralConnections: { ...foulSewersData.lateralConnections, table: [...lateralsInSync, newLateral] },
    });
  };

  const deleteCombinedRow = (index: number) => {
    updateField('foulSewers', {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, table: _pipeRuns.filter((_, i) => i !== index) },
      lateralConnections: { ...foulSewersData.lateralConnections, table: _laterals.filter((_, i) => i !== index) },
    });
  };

  const deleteCombinedRows = (indices: number[]) => {
    const idxSet = new Set(indices);
    updateField('foulSewers', {
      ...foulSewersData,
      pipeRuns: { ...foulSewersData.pipeRuns, table: _pipeRuns.filter((_, i) => !idxSet.has(i)) },
      lateralConnections: { ...foulSewersData.lateralConnections, table: _laterals.filter((_, i) => !idxSet.has(i)) },
    });
  };

  // NOTE: `Manhole.type` is a free-text field (whatever the real Excel "Type"
  // column represents, e.g. construction type) — it is NOT a controlled
  // 'foul'/'storm'/'both' enum, so filtering by it here excluded every
  // imported manhole from the lateral "Manhole Ref" dropdown. Foul/Storm
  // manholes already live in separate arrays (foulSewersData.manholes vs
  // stormSewersData.manholes), so no further filtering by type is needed.
  const foulManholes = availableManholes;

  const combinedFoulTakeOffColumns = [
    // ── Main Sewer Runs group ──────────────────────────────────────────────────
    {
      title: (
        <div style={{ textAlign: 'center', fontWeight: 700, color: '#1e40af', background: '#dbeafe', padding: '4px 8px', borderRadius: '4px' }}>
          Main Sewer Runs
        </div>
      ),
      key: 'mainSewerGroup',
      children: [
        {
          title: colHeader('Run Ref'),
          dataIndex: 'id',
          key: 'runRef',
          width: 90,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Input size="small" value={run.ref || ''} onChange={(e) => handleCellEdit('pipeRuns', run.id, 'ref', e.target.value)} />
            );
          },
        },
        {
          title: colHeader('Manhole From'),
          dataIndex: 'id',
          key: 'fromMH',
          width: 120,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={run.fromMH} onChange={(v) => updatePipeRun(run.id, 'fromMH', v)} placeholder="—" style={{ width: '100%' }} allowClear>
                {availableManholes.filter(mh => mh.ref).map(mh => <Select.Option key={mh.id} value={mh.ref}>{mh.ref}</Select.Option>)}
              </Select>
            );
          },
        },
        {
          title: colHeader('Manhole To'),
          dataIndex: 'id',
          key: 'toMH',
          width: 110,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={run.toMH} onChange={(v) => updatePipeRun(run.id, 'toMH', v)} placeholder="—" style={{ width: '100%' }} allowClear>
                {availableManholes.filter(mh => mh.ref).map(mh => <Select.Option key={mh.id} value={mh.ref}>{mh.ref}</Select.Option>)}
              </Select>
            );
          },
        },
        {
          title: colHeaderCalc('Depth From', 'm', {
            formula: 'Lookup: From Manhole Ref → Manhole Schedule: Depth (cover level − invert level)',
            rows: _pipeRuns.map((r) => ({ ref: r.fromMH || r.id, value: r.depthFrom ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'depthFrom',
          width: 120,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.depthFrom ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Depth To', 'm', {
            formula: 'Lookup: To Manhole Ref → Manhole Schedule: Depth (cover level − invert level)',
            rows: _pipeRuns.map((r) => ({ ref: r.toMH || r.id, value: r.depthTo ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'depthTo',
          width: 105,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.depthTo ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('Road Const Depth', 'm'),
          dataIndex: 'id',
          key: 'roadConstDepth',
          width: 150,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.roadConstDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Depth', 'm', {
            formula: '(Depth From + Depth To) / 2 − Road Construction Depth',
            rows: _pipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.averageDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'avgDepth',
          width: 110,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.averageDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Depth Category', 'm', {
            formula: 'ROUND(Avg Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _pipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.runDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'runDepthCat',
          width: 185,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.runDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('Clay/Conc/Ultra/Rigi'),
          dataIndex: 'id',
          key: 'runPipeType',
          width: 185,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={run.pipeType || undefined} onChange={(v) => updatePipeRun(run.id, 'pipeType', v)} placeholder="—" style={{ width: '100%' }}>
                <Select.Option value="clay">Clay</Select.Option>
                <Select.Option value="concrete">Concrete</Select.Option>
                <Select.Option value="ultra">Ultra</Select.Option>
                <Select.Option value="rigi">Rigi</Select.Option>
              </Select>
            );
          },
        },
        {
          title: colHeader('Pipe Ø', 'mm'),
          dataIndex: 'id',
          key: 'runDiameter',
          width: 100,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <NumberInput
                size="small"
                value={parseInt((run.diameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updatePipeRun(run.id, 'diameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            );
          },
        },
        {
          title: colHeader('Run Length', 'm'),
          dataIndex: 'id',
          key: 'runLength',
          width: 105,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return <NumberInput size="small" value={run.length} onChange={(v) => updatePipeRun(run.id, 'length', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Backfill', 'm'),
          dataIndex: 'id',
          key: 'backfill',
          width: 100,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={migrateBackfillType(run.backfillType) || undefined} onChange={(v) => updatePipeRun(run.id, 'backfillType', v)} placeholder="—" style={{ width: '100%' }}>
                {BACKFILL_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                ))}
              </Select>
            );
          },
        },
        {
          title: colHeaderCalc('Concrete Protection', 'm', {
            formula: 'Run Length (m) if Depth Category − (Pipe Ø / 1000) ≤ 1.25m, else 0',
            rows: _pipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.concreteProtection ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'concreteProt',
          width: 150,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.concreteProtection ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Rockers', 'Nr', {
            formula: '2 per pipe run (one at each end)',
            rows: _pipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.rockers ?? 0, unit: 'Nr' })),
          }),
          dataIndex: 'id',
          key: 'rockers',
          width: 95,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{record.pipeRun.rockers ?? 0}</div>;
          },
        },
        {
          title: colHeaderCalc('Junction Size', undefined, {
            formula: 'Rigi ≥ 450mm → {Ø}mm Saddle  |  Concrete ≥ 525mm → Core Drill  |  else → Main Ø × Branch Ø mm Junction',
            rows: [],
          }),
          dataIndex: 'id',
          key: 'junctionSize',
          width: 210,
          align: 'left' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const text = calcFoulJunctionSize(record.pipeRun, record.lateral);
            return (
              <div style={{ ...calcCellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>
                {text || <span style={{ color: '#d1d5db' }}>—</span>}
              </div>
            );
          },
        },
        {
          title: colHeader('Junctions', 'Nr'),
          dataIndex: 'id',
          key: 'junctions',
          width: 90,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return <NumberInput size="small" value={run.junctions ?? 0} onChange={(v) => updatePipeRun(run.id, 'junctions', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
      ],
    },
    // ── House Laterals group ───────────────────────────────────────────────────
    {
      title: (
        <div style={{ textAlign: 'center', fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>
          House Laterals
        </div>
      ),
      key: 'houseLateralsGroup',
      children: [
        {
          title: colHeader('Manhole Ref'),
          dataIndex: 'id',
          key: 'latManholeRef',
          width: 110,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return (
              <Select size="small" value={lat.toManhole} onChange={(v) => updateLateral(lat.id, 'toManhole', v)} placeholder="—" style={{ width: '100%' }} allowClear>
                {foulManholes.filter(mh => mh.ref).map(mh => <Select.Option key={mh.id} value={mh.ref}>{mh.ref}</Select.Option>)}
              </Select>
            );
          },
        },
        {
          title: colHeader('Junction Off Run', 'Nr'),
          dataIndex: 'id',
          key: 'junctionOffRun',
          width: 150,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.junctionOffRun ?? 0} onChange={(v) => updateLateral(lat.id, 'junctionOffRun', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Clay/Conc/Ultra/Rigi'),
          dataIndex: 'id',
          key: 'latPipeType',
          width: 185,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return (
              <Select size="small" value={lat.pipeType || undefined} onChange={(v) => updateLateral(lat.id, 'pipeType', v)} placeholder="—" style={{ width: '100%' }}>
                <Select.Option value="clay">Clay</Select.Option>
                <Select.Option value="concrete">Concrete</Select.Option>
                <Select.Option value="ultra">Ultra</Select.Option>
                <Select.Option value="rigi">Rigi</Select.Option>
              </Select>
            );
          },
        },
        {
          title: colHeader('Pipe Ø', 'mm'),
          dataIndex: 'id',
          key: 'latPipeDiameter',
          width: 100,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return (
              <NumberInput
                size="small"
                value={parseInt((lat.pipeDiameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updateLateral(lat.id, 'pipeDiameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            );
          },
        },
        {
          title: colHeader('Length', 'm'),
          dataIndex: 'id',
          key: 'latLength',
          width: 85,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.length ?? 0} onChange={(v) => updateLateral(lat.id, 'length', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Demarc Cover', 'm'),
          dataIndex: 'id',
          key: 'demarcCover',
          width: 120,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.demarcCoverLevel ?? 0} onChange={(v) => updateLateral(lat.id, 'demarcCoverLevel', v)} decimalPlaces={3} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Demarc Invert', 'm'),
          dataIndex: 'id',
          key: 'demarcInvert',
          width: 130,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.demarcInvertLevel ?? 0} onChange={(v) => updateLateral(lat.id, 'demarcInvertLevel', v)} decimalPlaces={3} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeaderCalc('Demarc Depth', 'm', {
            formula: 'Demarc Cover Level − Demarc Invert Level',
            rows: _laterals.map((l) => ({ ref: l.toManhole || l.id, value: l.demarcDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'demarcDepth',
          width: 135,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.demarcDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Depth Category', 'm', {
            formula: 'ROUND(Demarc Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _laterals.map((l) => ({ ref: l.toManhole || l.id, value: l.demarcDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'demarcDepthCat',
          width: 150,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.demarcDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Ave Run Depth', 'm', {
            formula: '(Run Depth Category + Demarc Depth Category) / 2',
            rows: _laterals.map((l) => ({ ref: l.toManhole || l.id, value: l.averageDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'aveRunDepth',
          width: 145,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.averageDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Run Category', 'm', {
            formula: 'ROUND(Ave Run Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _laterals.map((l) => ({ ref: l.toManhole || l.id, value: l.avgDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'avgRunCat',
          width: 170,
          align: 'center' as const,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.avgDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('NME', 'Nr'),
          dataIndex: 'id',
          key: 'nme',
          width: 75,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.nme ?? 0} onChange={(v) => updateLateral(lat.id, 'nme', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('1200mmØ chamber', 'Nr'),
          dataIndex: 'id',
          key: 'count1200',
          width: 130,
          render: (_: unknown, record: FoulTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.count1200 ?? 0} onChange={(v) => updateLateral(lat.id, 'count1200', v)} decimalPlaces={2} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
      ],
    },
    // ── Delete action column ───────────────────────────────────────────────────
    {
      title: 'Actions',
      key: 'combinedActions',
      width: 80,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: unknown, record: FoulTakeOffRow) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => requestDelete(() => deleteCombinedRow(record.index))}
        />
      ),
    },
  ];

  // ── Foul Summary helpers ───────────────────────────────────────────────────

  const pipeTypeLabel = (pt: string) => {
    const m: Record<string, string> = { clay: 'Clay', plastic: 'Plastic', concrete: 'Concrete', ultra: 'Ultra Rib', rigi: 'Rigi' };
    return m[pt] || pt;
  };

  // =IF(U9="clay","PS",IF(U9="Ultra","PU",IF(U9="Conc","PM",IF(U9="Rigi","PR",""))))
  const pipeTypeEvalCode = (pt: string): string => {
    const m: Record<string, string> = { clay: 'PS', ultra: 'PU', concrete: 'PM', rigi: 'PR' };
    return m[pt?.toLowerCase()] ?? '';
  };

  const summaryCols = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 140,
      align: 'center' as const,
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      dataIndex: 'evaluateCode',
      key: 'evaluateCode',
      width: 160,
      render: (v: string) => {
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(v);
        return (
          <span>
            <Tag color="blue">{v}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  ];

  const drainageRunsRows = (foulSewersData.pipeRuns.table || []).map((run, i) => {
    // Formula mirrors 'Foul Summary' sheet:
    // C9 & "-" & D9 & " - " & K9 & "mmØ, GBS, backfill with " & M9 &
    // ", cart surplus on site - dti " & TEXT(I9,"0.00") & "m."
    const diamNum = (run.diameter || '').replace(/mm$/i, '');
    const bf = backfillLabel(run.backfillType ?? '');
    const dti = (run.runDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(run.pipeType ?? '');
    const evalCode = diamNum && run.backfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(run.backfillType)}\\${dti}`
      : '';
    const runLength = run.length || 0;
    // Only pair fromMH/toMH into a "A-B" reference when both manholes are actually
    // selected — blank take-off rows (padded rows, or rows a user hasn't filled in
    // yet) have no manhole chosen and previously rendered as "undefined-undefined".
    const refLabel = run.fromMH && run.toMH ? `${run.fromMH}-${run.toMH}` : (run.fromMH || run.toMH || '');
    return {
      key: run.id || `run-${i}`,
      reference: run.ref || refLabel,
      description: `${refLabel ? `${refLabel} - ` : ''}${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: runLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label: refLabel ? `Run ${refLabel}` : 'Run',
        formula: `Pipe run length from take-off data · ${diamNum}mmØ ${run.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: refLabel || run.id, value: runLength, unit: 'm' }],
        total: runLength,
      } as FormulaInfo,
    };
  });

  const manholeRows = availableManholes.map((mh, i) => {
    // Formula mirrors 'Foul Manhole Schedule' Foul Summary sheet:
    // B9 & " - " & E9 & "mmØ," & SWITCH(D9,"easi"," Wide wall chamber","trad","Traditional chamber","") & " dti - " & TEXT(I9,"0.00") & "m"
    const diamNum = (mh.diameter || '').replace(/mm$/i, '');
    const chamberType = mh.easiTrad === 'easi'
      ? ' Wide wall chamber'
      : mh.easiTrad === 'trad'
      ? 'Traditional chamber'
      : '';
    const dti = (mh.depthCategory || 0).toFixed(2);
    // Evaluate code: {Easi|Trad}\{diameter}\{depth}  e.g. Easi\1200\1.50
    const chamberCode = mh.easiTrad === 'easi' ? 'Easi' : mh.easiTrad === 'trad' ? 'Trad' : '';
    const evalCode = chamberCode && diamNum ? `${chamberCode}\\${diamNum}\\${dti}` : '';
    return {
      key: mh.id || `mh-${i}`,
      reference: mh.ref,
      description: `${mh.ref} - ${diamNum}mmØ,${chamberType} dti - ${dti}m`,
      quantity: '1',
      unit: 'Nr',
      evaluateCode: evalCode,
      formulaInfo: {
        label: mh.ref,
        formula: `Manhole from take-off data · ${diamNum}mmØ · ${mh.easiTrad ?? ''} chamber · depth to invert: ${dti}m`,
        rows: [{ ref: mh.ref, value: 1, unit: 'Nr' }],
        total: 1,
      } as FormulaInfo,
    };
  });

  const lateralRows = (foulSewersData.lateralConnections.table || []).map((lat, i) => {
    const diamNum = (lat.pipeDiameter || '').replace(/mm$/i, '');
    // House Laterals have no backfill field of their own — the source sheet's
    // description formula reads the paired Main Sewer Run's Backfill column,
    // matched positionally by row (same convention as Gully Laterals).
    const runBackfillType = (foulSewersData.pipeRuns.table || [])[i]?.backfillType;
    const bf = backfillLabel(runBackfillType ?? '');
    const dti = (lat.avgDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(lat.pipeType ?? '');
    const evalCode = diamNum && runBackfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(runBackfillType)}\\${dti}`
      : '';
    const latLength = lat.length || 0;
    const mhRef = lat.toManhole || '';
    return {
      key: lat.id || `lat-${i}`,
      reference: mhRef,
      description: `${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: latLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label: mhRef,
        formula: `Lateral connection length from take-off data · ${diamNum}mmØ ${lat.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: mhRef, value: latLength, unit: 'm' }],
        total: latLength,
      } as FormulaInfo,
    };
  });

  const SUMMARY_SECTIONS = [
    { key: 'drainage-runs', title: 'Drainage Runs', rows: drainageRunsRows, emptyText: 'No pipe runs entered yet' },
    { key: 'manholes', title: 'Manholes', rows: manholeRows, emptyText: 'No manholes entered yet' },
    { key: 'lateral-connections', title: 'Lateral Connections', rows: lateralRows, emptyText: 'No lateral connections entered yet' },
  ];

  type SummaryRowType = 'section' | 'pipe-type' | 'item';

  interface FormulaInfoRow { ref: string; value: number; unit: string }
  interface FormulaInfo {
    label: string;
    formula: string;
    rows: FormulaInfoRow[];
    total: number;
  }

  interface SummaryRow {
    key: string;
    rowType: SummaryRowType;
    reference?: string;
    description?: string;
    quantity?: string;
    unit?: string;
    evaluateCode?: string;
    emptyText?: string;
    formulaInfo?: FormulaInfo;
    children?: SummaryRow[];
  }

  const pipeRuns = foulSewersData.pipeRuns.table || [];

  const getJunctionQty = (pipeType: string, mainDia: string, branchDia: string): number => {
    if (!branchDia) return 0;
    return pipeRuns
      .filter(
        (run, i) =>
          (run.pipeType ?? '').toLowerCase() === pipeType.toLowerCase() &&
          run.diameter === mainDia &&
          _laterals[i]?.pipeDiameter === branchDia,
      )
      .reduce((sum, run) => sum + (run.junctions ?? 0), 0);
  };

  // Count junctions for a specific saddle diameter (rigi pipes at exact mainDia)
  const getSaddleQty = (pipeType: string, mainDia: string): number =>
    pipeRuns
      .filter(
        (run) =>
          (run.pipeType ?? '').toLowerCase() === pipeType.toLowerCase() &&
          run.diameter === mainDia,
      )
      .reduce((sum, run) => sum + (run.junctions ?? 0), 0);

  // Count junctions for concrete core drill (all concrete pipes >= 525mm)
  const getCoreDrillQty = (): number =>
    pipeRuns
      .filter(
        (run) =>
          (run.pipeType ?? '').toLowerCase() === 'concrete' &&
          (parseInt((run.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
      )
      .reduce((sum, run) => sum + (run.junctions ?? 0), 0);

  // ── Manhole accessory rows (R208–R228 of Foul Summary) ────────────────────
  const manholes = availableManholes;

  const ladderCfgLabel = foulSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised';
  const safetyCfgLabel = foulSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised';

  const manholeAccessoryItems: SummaryRow[] = [
    {
      key: 'mh-ladders',
      rowType: 'item',
      description: `Manhole ladders (if not incl in manhole price)${ladderCfgLabel !== 'N/A' ? ` - ${ladderCfgLabel}` : ''}`,
      quantity: calcLadderMetres(manholes).toFixed(3),
      unit: 'm',
      formulaInfo: {
        label: 'Manhole Ladders',
        formula: 'SUM(ladders) across all manholes — value entered per manhole row',
        rows: manholes.filter((mh) => (mh.ladders ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.ladders ?? 0, unit: 'm' })),
        total: calcLadderMetres(manholes),
      },
    },
    {
      key: 'mh-chains',
      rowType: 'item',
      description: `Safety Chains${safetyCfgLabel !== 'N/A' ? ` - ${safetyCfgLabel}` : ''}`,
      quantity: calcChainMetres(manholes).toFixed(3),
      unit: 'Nr',
      formulaInfo: {
        label: 'Safety Chains',
        formula: 'SUM(safetyChains) across all manholes — value entered per manhole row',
        rows: manholes.filter((mh) => (mh.safetyChains ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.safetyChains ?? 0, unit: 'Nr' })),
        total: calcChainMetres(manholes),
      },
    },
    {
      key: 'mh-amps',
      rowType: 'item',
      description: 'AMPS',
      quantity: String(manholes.reduce((sum, mh) => sum + (mh.amps ?? 0), 0)),
      unit: 'Nr',
      formulaInfo: {
        label: 'AMPS',
        formula: 'Sum amps count across all manholes',
        rows: manholes.filter((mh) => (mh.amps ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.amps ?? 0, unit: 'Nr' })),
        total: manholes.reduce((sum, mh) => sum + (mh.amps ?? 0), 0),
      },
    },
    {
      key: 'mh-backdrops',
      rowType: 'item',
      description: 'Backdrops',
      quantity: String(manholes.reduce((sum, mh) => sum + (mh.backdrop ?? 0), 0)),
      unit: 'Nr',
      formulaInfo: {
        label: 'Backdrops',
        formula: 'Sum backdrop count across all manholes',
        rows: manholes.filter((mh) => (mh.backdrop ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.backdrop ?? 0, unit: 'Nr' })),
        total: manholes.reduce((sum, mh) => sum + (mh.backdrop ?? 0), 0),
      },
    },
    {
      key: 'mh-muckstoppers',
      rowType: 'item',
      description: 'Muckstoppers',
      quantity: String(manholes.reduce((sum, mh) => sum + (mh.muckStopper ?? 0), 0)),
      unit: 'Nr',
      formulaInfo: {
        label: 'Muckstoppers',
        formula: 'Sum muckStopper count across all manholes',
        rows: manholes.filter((mh) => (mh.muckStopper ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.muckStopper ?? 0, unit: 'Nr' })),
        total: manholes.reduce((sum, mh) => sum + (mh.muckStopper ?? 0), 0),
      },
    },
    {
      key: 'mh-clegg',
      rowType: 'item',
      description: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)",
      quantity: '',
      unit: 'Item',
    },
  ];

  const manholeAdjustmentRow: SummaryRow = {
    key: 'mh-adjustment',
    rowType: 'pipe-type',
    reference: 'ADJUSTMENT FOR ROAD CONSTRUCTION GREATER THAN 450mm',
    children: [
      {
        key: 'mh-credits',
        rowType: 'pipe-type',
        reference: 'Credits',
        children: [
          {
            key: 'mh-adj-trench', rowType: 'item', description: 'Trench Excavation',
            quantity: eoConcBedQtys['mh-adj-trench'] ? String(eoConcBedQtys['mh-adj-trench']) : '', unit: 'm3',
          },
          {
            key: 'mh-adj-ews', rowType: 'item', description: 'EWS',
            quantity: eoConcBedQtys['mh-adj-ews'] ? String(eoConcBedQtys['mh-adj-ews']) : '', unit: 'm2',
          },
          {
            key: 'mh-adj-disposal', rowType: 'item', description: 'Disposal',
            quantity: eoConcBedQtys['mh-adj-disposal'] ? String(eoConcBedQtys['mh-adj-disposal']) : '', unit: 'm3',
          },
          {
            key: 'mh-adj-backfill', rowType: 'item', description: 'Backfill',
            quantity: eoConcBedQtys['mh-adj-backfill'] ? String(eoConcBedQtys['mh-adj-backfill']) : '', unit: 'm3',
          },
        ],
      },
    ],
  };

  const existingSummaryData: SummaryRow[] = SUMMARY_SECTIONS.map(({ key, title, rows, emptyText }) => ({
    key,
    rowType: 'section' as const,
    description: title,
    emptyText,
    children: rows.length > 0
      ? rows.map((r) => ({ ...r, rowType: 'item' as const }))
      : [{ key: `${key}-empty`, rowType: 'item' as const, description: emptyText }],
  }));

  // ── Rocker / Stub Pipes section ───────────────────────────────────────────
  const stubPipesSection: SummaryRow = {
    key: 'stub-pipes',
    rowType: 'pipe-type',
    reference: 'Rocker / Stub Pipes',
    children: FOUL_STUB_PIPE_GROUPS.map((group) => ({
      key: group.key,
      rowType: 'pipe-type' as SummaryRowType,
      reference: group.label,
      children: group.items.map((item) => {
        const qty = calcRockerQty(pipeRuns, group.pipeType, item.dia);
        return {
          key: `${group.key}-${item.code || item.desc}`,
          rowType: 'item' as SummaryRowType,
          description: item.desc,
          quantity: String(qty),
          unit: 'Nr',
          evaluateCode: item.code || undefined,
          formulaInfo: {
            label: item.desc,
            formula: `Sum rockers from all pipe runs where pipeType = '${group.pipeType}' AND diameter = ${item.dia}mm`,
            rows: pipeRuns
              .filter((r) => (r.pipeType ?? '').toLowerCase() === group.pipeType.toLowerCase() && parseInt(r.diameter ?? '', 10) === item.dia)
              .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.rockers ?? 0, unit: 'Nr' })),
            total: qty,
          } as FormulaInfo,
        };
      }),
    })),
  };

  // ── Junctions section ─────────────────────────────────────────────────────
  const junctionsSection: SummaryRow = {
    key: 'junctions',
    rowType: 'pipe-type',
    reference: 'Junctions',
    children: [
      ...FOUL_JUNCTION_GROUPS.map((group) => ({
        key: group.key,
        rowType: 'pipe-type' as SummaryRowType,
        reference: group.label,
        children: group.items.map((item, idx) => {
          let qty = 0;
          let formulaInfo: FormulaInfo | undefined;

          if (item.branchDia) {
            qty = getJunctionQty(group.pipeType, item.mainDia, item.branchDia);
            const matchingRuns = pipeRuns.filter(
              (r, i) =>
                (r.pipeType ?? '').toLowerCase() === group.pipeType.toLowerCase() &&
                r.diameter === item.mainDia &&
                _laterals[i]?.pipeDiameter === item.branchDia,
            );
            formulaInfo = {
              label: item.desc,
              formula: `Sum junctions from pipe runs where pipeType = '${group.pipeType}', pipe diameter = ${item.mainDia}, lateral diameter = ${item.branchDia}`,
              rows: matchingRuns.map((r) => ({
                ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id,
                value: r.junctions ?? 0,
                unit: 'Nr',
              })),
              total: qty,
            };
          } else if (group.pipeType === 'rigi') {
            qty = getSaddleQty(group.pipeType, item.mainDia);
            const matchingRuns = pipeRuns.filter(
              (r) =>
                (r.pipeType ?? '').toLowerCase() === 'rigi' &&
                r.diameter === item.mainDia,
            );
            formulaInfo = {
              label: item.desc,
              formula: `Sum junctions from Ridgisewer pipe runs where diameter = ${item.mainDia} (saddle required)`,
              rows: matchingRuns.map((r) => ({
                ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id,
                value: r.junctions ?? 0,
                unit: 'Nr',
              })),
              total: qty,
            };
          } else if (group.pipeType === 'concrete') {
            qty = getCoreDrillQty();
            const matchingRuns = pipeRuns.filter(
              (r) =>
                (r.pipeType ?? '').toLowerCase() === 'concrete' &&
                (parseInt((r.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
            );
            formulaInfo = {
              label: item.desc,
              formula: `Sum junctions from Concrete pipe runs where diameter >= 525mm (core drill required)`,
              rows: matchingRuns.map((r) => ({
                ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id,
                value: r.junctions ?? 0,
                unit: 'Nr',
              })),
              total: qty,
            };
          }

          return {
            key: `${group.key}-${item.code || idx}`,
            rowType: 'item' as SummaryRowType,
            description: item.desc,
            quantity: qty > 0 ? String(qty) : '',
            unit: 'Nr',
            evaluateCode: item.code || undefined,
            formulaInfo,
          };
        }),
      })),
      {
        key: 'fjunc-eo-conc-bed',
        rowType: 'pipe-type' as SummaryRowType,
        reference: 'EO Concrete Bed & Surround',
        children: FOUL_EO_CONC_BED_ITEMS.map((item, idx) => ({
          key: `feo-conc-bed-${idx}`,
          rowType: 'item' as SummaryRowType,
          description: item.desc,
          quantity: '',
          unit: 'm',
          evaluateCode: item.code,
        })),
      },
      {
        key: 'fjunc-conc-protection',
        rowType: 'pipe-type' as SummaryRowType,
        reference: 'Concrete Protection Slab',
        children: FOUL_CONC_PROTECTION_ITEMS.map((item, idx) => {
          const qty = calcConcreteProtection(pipeRuns, item.dia);
          return {
            key: `fconc-protection-${idx}`,
            rowType: 'item' as SummaryRowType,
            description: item.desc,
            quantity: String(qty),
            unit: 'm',
            evaluateCode: item.code,
            formulaInfo: {
              label: item.desc,
              formula: `Sum concreteProtection (m) from all pipe runs where diameter = ${item.dia}mm`,
              rows: pipeRuns
                .filter((r) => parseInt(r.diameter ?? '', 10) === item.dia)
                .map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.concreteProtection ?? 0, unit: 'm' })),
              total: qty,
            } as FormulaInfo,
          };
        }),
      },
    ],
  };

  // ── EO Items section ──────────────────────────────────────────────────────
  const eoItemsSection: SummaryRow = {
    key: 'eo-items',
    rowType: 'pipe-type',
    reference: 'EO Items',
    children: FOUL_EO_ITEMS.map((item, idx) => ({
      key: `feo-item-${idx}`,
      rowType: 'item' as SummaryRowType,
      description: item.desc,
      quantity: '',
      unit: item.unit,
      evaluateCode: item.code,
    })),
  };

  // ── Removal of Redundant Sewer section ───────────────────────────────────
  const redundantSewerSection: SummaryRow = {
    key: 'redundant-sewer',
    rowType: 'pipe-type',
    reference: 'Removal of Redundant Sewer',
    children: FOUL_REDUNDANT_SEWER_ITEMS.map((item, idx) => ({
      key: `fredundant-${idx}`,
      rowType: 'item' as SummaryRowType,
      description: item.desc,
      quantity: '',
      unit: item.unit,
      evaluateCode: undefined,
    })),
  };

  // Order: Drainage Runs, Rocker/Stub Pipes, Junctions, EO Items, Removal of
  // Redundant Sewer, Manholes, Lateral Connections
  const [drSection, , lateralSection] = existingSummaryData;

  // Build manholes section with accessory rows appended after per-manhole items
  const manholeBaseRows = manholeRows.map((r) => ({ ...r, rowType: 'item' as SummaryRowType }));
  const customManholeSection: SummaryRow = {
    key: 'manholes',
    rowType: 'section',
    description: 'Manholes',
    children: [
      ...(manholeBaseRows.length > 0
        ? manholeBaseRows
        : [{ key: 'manholes-empty', rowType: 'item' as SummaryRowType, description: 'No manholes entered yet' }]),
      ...manholeAccessoryItems,
      manholeAdjustmentRow,
    ],
  };

  const nmeQty = (foulSewersData.lateralConnections.table || []).reduce((sum, lat) => sum + (lat.nme ?? 0), 0);
  // The "1200" column holds each chamber's own depth (e.g. 3.25), not a Nr
  // count — so the chamber total is how many laterals have a depth entered,
  // and the depth breakdown below counts how many share each depth.
  const laterals1200 = (foulSewersData.lateralConnections.table || []).filter((lat) => (lat.count1200 ?? 0) > 0);

  // 1200mmØ chamber depth breakdown — av 3.00m deep .. av 6.00m deep, in 0.25m steps.
  const DEPTH_BUCKETS_1200 = Array.from({ length: 13 }, (_, i) => (3 + i * 0.25).toFixed(2));
  const count1200DepthRows: SummaryRow[] = DEPTH_BUCKETS_1200.map((d) => {
    const matching = laterals1200.filter((lat) => (lat.count1200 ?? 0).toFixed(2) === d);
    const qty = matching.length;
    return {
      key: `flat-1200-chamber-${d}`,
      rowType: 'item' as SummaryRowType,
      description: `av ${d}m deep`,
      quantity: qty > 0 ? String(qty) : '',
      unit: 'Nr',
      formulaInfo: {
        label: `1200mmØ chamber av ${d}m deep`,
        formula: `COUNT(house laterals) where 1200 column = ${d}m`,
        rows: matching.map((lat) => ({ ref: lat.toManhole || '', value: 1, unit: 'Nr' })),
        total: qty,
      },
    };
  });

  const customLateralSection: SummaryRow = {
    ...lateralSection,
    rowType: 'pipe-type',
    description: undefined,
    reference: 'Lateral Connections',
    children: [
      ...(lateralSection.children ?? []),
      {
        key: 'flat-nme-chamber',
        rowType: 'item' as SummaryRowType,
        description: 'NME Chamber',
        quantity: nmeQty > 0 ? String(nmeQty) : '',
        unit: 'Nr',
        formulaInfo: {
          label: 'NME Chamber',
          formula: 'SUM(nme) across all house laterals',
          rows: (foulSewersData.lateralConnections.table || [])
            .filter((lat) => (lat.nme ?? 0) > 0)
            .map((lat) => ({ ref: lat.toManhole || '', value: lat.nme ?? 0, unit: 'Nr' })),
          total: nmeQty,
        },
      },
      {
        key: 'flat-1200-chamber',
        rowType: 'item' as SummaryRowType,
        description: '1200mmØ chamber',
        quantity: '',
        unit: 'Nr',
      },
      ...count1200DepthRows,
    ],
  };

  // Only two top-level parent sections remain — Drainage Runs and Manholes.
  // Every other former top-level section (Rocker/Stub Pipes, Junctions, EO
  // Items, Removal of Redundant Sewer, Lateral Connections) now nests as a
  // pipe-type group under Drainage Runs.
  const customDrSection: SummaryRow = {
    ...drSection,
    children: [
      ...(drSection.children ?? []),
      stubPipesSection,
      junctionsSection,
      eoItemsSection,
      redundantSewerSection,
      customLateralSection,
    ],
  };

  const summaryTableData: SummaryRow[] = [
    customDrSection,
    customManholeSection,
  ];

  // ── Surplus / Backfill tables — mirrors 'Foul Summary' sheet's AE:AH
  // (Surplus Runs/Lateral/Manholes/Total) and AJ:AM (Backfill Runs/Laterals/
  // Manholes/Total) columns, one row per pipe run. See
  // buildFoulSurplusBackfillData (foulSummaryUtils.ts) for the formula detail.
  const surplusBackfillData = buildFoulSurplusBackfillData(pipeRuns, manholes, _laterals);

  const SUMMARY_COL_COUNT = showDevMode ? 5 : 4;

  const summaryTableCols = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (v: string, record: SummaryRow) => {
        // Total-in-accordion display for Drainage Runs commented out in the UI —
        // now that it nests Stub Pipes/Junctions/EO Items/Laterals/Gully sub-groups,
        // it collapses like any other section instead of reserving 2 columns for a total.
        // if (record.key === 'drainage-runs' && record.rowType === 'section')
        //   return { children: <Typography.Text strong className="text-indigo-600">{record.description}</Typography.Text>, props: { colSpan: 2 } };
        if (record.rowType === 'section')
          return { children: <Typography.Text strong className="text-indigo-600">{record.description}</Typography.Text>, props: { colSpan: SUMMARY_COL_COUNT } };
        if (record.rowType === 'pipe-type')
          return { children: <Typography.Text strong style={{ color: '#374151' }}>{record.reference}</Typography.Text>, props: { colSpan: SUMMARY_COL_COUNT } };
        return v ? <Typography.Text strong>{v}</Typography.Text> : null;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 400,
      render: (v: string, record: SummaryRow) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        return (
          <span>
            {v}
            {showDevMode && record.formulaInfo && (
              <InfoCircleOutlined
                style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFormulaInfoKey(record.key);
                }}
              />
            )}
          </span>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      align: 'center' as const,
      render: (v: string, record: SummaryRow) => {
        // Total-in-accordion display for Drainage Runs commented out in the UI —
        // it summed only pipe-run metres (nested Nr/m³ sub-groups contributed 0),
        // but now collapses like any other section instead of showing that total.
        // if (record.key === 'drainage-runs' && record.rowType === 'section') {
        //   const total = (record.children ?? []).reduce((sum, c) => sum + (parseFloat(c.quantity || '0') || 0), 0);
        //   return <Typography.Text strong>{total.toFixed(2)}</Typography.Text>;
        // }
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        if (record.key.startsWith('mh-adj-')) {
          return (
            <NumberInput
              size="small"
              value={eoConcBedQtys[record.key] ?? 0}
              onChange={(val) => setEoConcBedQty(record.key, val)}
              isInteger
              decimalPlaces={0}
              style={{ width: '80px', textAlign: 'right' }}
            />
          );
        }
        if (
          record.key.startsWith('feo-conc-bed-') ||
          record.key.startsWith('feo-item-') ||
          record.key.startsWith('fredundant-') ||
          record.key === 'mh-clegg'
        ) {
          return (
            <NumberInput
              size="small"
              value={eoConcBedQtys[record.key] ?? 0}
              onChange={(val) => setEoConcBedQty(record.key, val)}
              isInteger
              positiveOnly
              decimalPlaces={0}
              style={{ width: '80px', textAlign: 'right' }}
            />
          );
        }
        if (v == null) return '—';
        if (record.key === 'flat-1200-chamber' && v === '') return '';
        const n = parseFloat(v);
        return isNaN(n) ? '0' : Math.round(n).toLocaleString('en-GB');
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      align: 'center' as const,
      render: (v: string, record: SummaryRow) => {
        // if (record.key === 'drainage-runs' && record.rowType === 'section') {
        //   return <Typography.Text strong>{formatUnit('m')}</Typography.Text>;
        // }
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        return formatUnit(v) || null;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      dataIndex: 'evaluateCode',
      key: 'evaluateCode',
      width: 160,
      render: (v: string, record: SummaryRow) => {
        // if (record.key === 'drainage-runs' && record.rowType === 'section') return null;
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(v);
        return (
          <span>
            <Tag color="blue">{v}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  ];

  // Numeric scroll.x (sum of column widths) forces table-layout: fixed, which
  // the sticky-header prop below needs to keep the header/body columns in sync.
  const SUMMARY_TABLE_SCROLL_X = 180 + 400 + 120 + 80 + (showDevMode ? 160 : 0);

  // ── Build Up helpers ──────────────────────────────────────────────────────
  // Pump Station rows (id prefix 'ps_') live in state.pumpStation.table and
  // Rising Main rows (id prefix 'rm_') live in state.risingMain.table — merge
  // for read purposes (ids are disjoint by prefix), route writes back to
  // whichever top-level field the row's id prefix indicates.
  const buildUpData: Record<string, FoulBuildUpRowData> = {
    ...state.pumpStation.table,
    ...state.risingMain.table,
  };

  const getBuildUpRow = (id: string, defaultQuant: number | null) => ({
    quant: buildUpData[id]?.quant !== undefined ? buildUpData[id].quant : defaultQuant,
  });

  // Mesh reinforcement rows' code depends on the user-selected mesh type, and the
  // Wet Well row's code depends on the configured chamber diameter/depth — both
  // computed live via the shared resolver (also used by `buildBuildUpSummaryData`
  // for the /roads-sewers/review Bill Summary, so both stay in sync).
  const getBuildUpEvaluateCode = (record: BuildUpRowDef): string =>
    resolveBuildUpEvaluateCode(record, savedPumpChamberConfig);

  // 'quant' only — evaluateCode is never written back to state/API, it's derived
  // live from foulBuildUpEvaluateCodesMapping.ts (see getBuildUpEvaluateCode below).
  const updateBuildUpRow = (id: string, field: 'quant', value: number | null) => {
    const current = buildUpData[id] ?? {};
    const updatedRow = { ...current, [field]: value };
    if (id.startsWith('ps_')) {
      updateField('pumpStation', {
        ...state.pumpStation,
        table: { ...state.pumpStation.table, [id]: updatedRow },
      });
    } else if (id.startsWith('rm_')) {
      updateField('risingMain', {
        ...state.risingMain,
        table: { ...state.risingMain.table, [id]: updatedRow },
      });
    }
  };


  const buildBuildUpTableColumns = (sectionTitle: string) => [
    {
      title: <span style={{ fontWeight: 700, fontSize: 13 }}>{sectionTitle}</span>,
      dataIndex: 'description',
      key: 'description',
      render: (_: string, record: BuildUpRowDef) => {
        if (record.isSeparator) return null;
        if (record.isHeader) {
          const defaultTitle = record.id === 'ps_vc_hdr' ? valveChamberHeaderText : record.description;
          // Only Pump Station section titles are editable (Rising Main's single header isn't).
          if (!record.id.startsWith('ps_')) {
            return <Typography.Text strong className="text-indigo-600">{defaultTitle}</Typography.Text>;
          }
          if (editingTitleId === record.id) {
            return (
              <span onClick={(e) => e.stopPropagation()}>
              <Input
                autoFocus
                size="small"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onPressEnter={() => saveSectionTitle(record.id)}
                onBlur={() => saveSectionTitle(record.id)}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditingTitleId(null); }}
                placeholder="Text to append to title"
                addonBefore={defaultTitle}
                className="section-title-input"
                style={{ width: 380 }}
              />
              </span>
            );
          }
          const appended = pumpStationSectionTitles?.[record.id];
          return (
            <Typography.Text strong className="text-indigo-600">
              {defaultTitle}
              {appended && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: '0 8px',
                    borderRadius: 4,
                    background: '#fff7e6',
                    border: '1px solid #ffd591',
                    color: '#d46b08',
                    fontWeight: 600,
                  }}
                >
                  {appended}
                </span>
              )}
              {!state.isViewMode && (
                <EditOutlined
                  title="Append text to title"
                  style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }}
                  onClick={() => { setTitleDraft(appended ?? ''); setEditingTitleId(record.id); }}
                />
              )}
            </Typography.Text>
          );
        }
        if (record.id === 'ps_pc_wetwell') {
          return <Typography.Text>{wetWellDescription}</Typography.Text>;
        }
        if (record.id === 'ps_7') {
          return <Typography.Text>{motType1SubBaseDescription}</Typography.Text>;
        }
        if (record.id === 'ps_11') {
          return <Typography.Text>{pav2SlabDescription}</Typography.Text>;
        }
        if (record.id === 'ps_10') {
          return <Typography.Text>{ps10MeshDescription}</Typography.Text>;
        }
        if (record.id === 'ps_pc_mesh') {
          return <Typography.Text>{pumpChamberMeshDescription}</Typography.Text>;
        }
        if (record.id === 'ps_vc_mesh') {
          return <Typography.Text>{valveChamberMeshDescription}</Typography.Text>;
        }
        if (record.id === 'ps_fm_mesh') {
          return <Typography.Text>{flowMeterChamberMeshDescription}</Typography.Text>;
        }
        if (record.id === 'ps_kb_mesh') {
          return <Typography.Text>{kioskBaseMeshDescription}</Typography.Text>;
        }
        if (record.id === 'ps_pc_blinding') {
          return <Typography.Text>{pumpChamberBlindingDescription}</Typography.Text>;
        }
        if (record.id === 'ps_vc_concrete') {
          return <Typography.Text>{valveChamberConcreteDescription}</Typography.Text>;
        }
        if (record.id === 'ps_fm_concrete') {
          return <Typography.Text>{flowMeterChamberConcreteDescription}</Typography.Text>;
        }
        if (record.id === 'ps_kb_concrete') {
          return <Typography.Text>{kioskBaseConcreteDescription}</Typography.Text>;
        }
        if (record.id === 'ps_2') {
          return (
            <Typography.Text>
              {topsoilStripDescription}
              {showDevMode && (
                <InfoCircleOutlined
                  style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                  onClick={() => setColCalcInfo({
                    title: 'Excavate to Reduce Levels (Topsoil Strip)',
                    formula: 'Description mm = Ex Topsoil Depth Utilised × 1000 (Global Settings)  |  Qty (m³) = Ex Topsoil Depth Utilised × Site strip to remove vegetation (whole area) qty',
                    unit: 'm3',
                    rows: [],
                  })}
                />
              )}
            </Typography.Text>
          );
        }
        return <Typography.Text>{record.description}</Typography.Text>;
      },
    },
    {
      title: 'Qty',
      dataIndex: 'id',
      key: 'quant',
      width: 120,
      align: 'center' as const,
      render: (_: string, record: BuildUpRowDef) => {
        if (record.isSeparator || record.isHeader) return null;
        if (record.id === 'ps_2') {
          const { quant: siteStripQty } = getBuildUpRow('ps_1', PUMP_STATION_ROWS.find(r => r.id === 'ps_1')?.defaultQuant ?? null);
          const calcQty = exTopsoilDepthUtilised * (siteStripQty ?? 0);
          return <Typography.Text style={{ display: 'block', textAlign: 'right', paddingRight:'10px' }}>{calcQty.toFixed(3)}</Typography.Text>;
        }
        const { quant } = getBuildUpRow(record.id, record.defaultQuant);
        return (
          <NumberInput
            value={quant ?? 0}
            onChange={(val) => updateBuildUpRow(record.id, 'quant', val)}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (_: string, record: BuildUpRowDef) => {
        if (record.isSeparator || record.isHeader) return null;
        const display = formatUnit(record.unit);
        return <span style={{ color: '#6b7280' }}>{display}</span>;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      dataIndex: 'id',
      key: 'evaluateCode',
      width: 160,
      render: (_: string, record: BuildUpRowDef) => {
        if (record.isSeparator || record.isHeader) return null;
        const evaluateCode = getBuildUpEvaluateCode(record);
        if (!evaluateCode) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(evaluateCode);
        return (
          <span>
            <Tag color="blue">{evaluateCode}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  ];

  const pumpStationContent = (
    <div>
      <Table<BuildUpRowDef>
        className="foul-sewers-table build-up-table"
        bordered
        size="middle"
        pagination={false}
        dataSource={applyPumpStationLadderType(PUMP_STATION_ROWS, foulSewersData.manholes.config.manholeScheduleConfig?.ladders, state.pumpStation.config as PumpChamberConfig).filter(r => !r.isSeparator)}
        rowKey="id"
        columns={buildBuildUpTableColumns('PUMPING STATION')}
        rowClassName={(record) => {
          if (record.isHeader) return 'build-up-header-row';
          return '';
        }}
      />
    </div>
  );

  const risingMainContent = (
    <div>
      <Table<BuildUpRowDef>
        className="foul-sewers-table build-up-table"
        bordered
        size="middle"
        pagination={false}
        dataSource={RISING_MAIN_ROWS.filter(r => !r.isSeparator)}
        rowKey="id"
        columns={buildBuildUpTableColumns('RISING MAIN')}
        rowClassName={(record) => {
          if (record.isHeader) return 'build-up-header-row';
          return '';
        }}
      />
    </div>
  );

  const summaryAllKeys = (() => {
    const collectKeys = (rows: SummaryRow[]): React.Key[] =>
      rows.flatMap((r) => r.children?.length ? [r.key, ...collectKeys(r.children)] : []);
    return collectKeys(summaryTableData);
  })();

  // Shared column shape for the Surplus and Backfill tables below — same
  // layout, different column labels. Clicking the Total cell's info icon
  // opens the surplusBackfillFormula modal (mirrors the main summary table's
  // formulaInfoKey modal above).
  const buildSurplusBackfillCols = (labels: { runs: string; laterals: string; manholes: string; total: string }) => [
    { title: 'Reference', dataIndex: 'reference', key: 'reference', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: `${labels.runs} (m³)`, dataIndex: 'runs', key: 'runs', width: 140, align: 'right' as const,
      render: (v: number, record: FoulSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setSurplusBackfillFormula(record.runsFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.laterals} (m³)`, dataIndex: 'laterals', key: 'laterals', width: 140, align: 'right' as const,
      render: (v: number, record: FoulSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setSurplusBackfillFormula(record.lateralsFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.manholes} (m³)`, dataIndex: 'manholes', key: 'manholes', width: 150, align: 'right' as const,
      render: (v: number, record: FoulSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setSurplusBackfillFormula(record.manholesFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.total} (m³)`, key: 'total', width: 150, align: 'right' as const,
      render: (_: unknown, record: FoulSurplusBackfillRow) => (
        <Typography.Text strong>{record.total.toFixed(3)}</Typography.Text>
      ),
    },
  ];

  const summaryContent = (
    <div>
      {summaryView === 'summary' && (
        <>
          <Table<SummaryRow>
            className="foul-summary-table"
            dataSource={summaryTableData}
            columns={summaryTableCols}
            pagination={false}
            size="middle"
            bordered
            scroll={{ x: SUMMARY_TABLE_SCROLL_X }}
            sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
            expandable={{
              childrenColumnName: 'children',
              expandedRowKeys: summaryExpandedKeys,
              onExpand: (expanded, record) => {
                setSummaryExpandedKeys((prev) =>
                  expanded ? [...prev, record.key] : prev.filter((k) => k !== record.key)
                );
              },
              expandRowByClick: true,
            }}
            rowClassName={(record) => {
              if (record.rowType === 'section') return 'bg-indigo-50 font-semibold';
              if (record.rowType === 'pipe-type') return 'bg-gray-50';
              return '';
            }}
          />
          {/* ── Formula info modal ──────────────────────────────────────────── */}
          {(() => {
            if (!formulaInfoKey) return null;
            const findRow = (rows: SummaryRow[], key: string): SummaryRow | null => {
              for (const r of rows) {
                if (r.key === key) return r;
                if (r.children) { const f = findRow(r.children, key); if (f) return f; }
              }
              return null;
            };
            const row = findRow(summaryTableData, formulaInfoKey);
            if (!row?.formulaInfo) return null;
            const { label, formula, total } = row.formulaInfo;
            const unit = row.unit ?? '';
            const fmt = (n: number) => Number.isInteger(n) ? String(n) : n.toFixed(3);
            return (
              <Modal
                title={`${label} — Quantity Formula`}
                open={true}
                onCancel={() => setFormulaInfoKey(null)}
                footer={[<Button key="close" onClick={() => setFormulaInfoKey(null)}>Close</Button>]}
                width={560}
              >
                <div style={{ fontSize: 13, color: '#374151' }}>
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {formula}
                  </div>
                  <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 4 }}>
                    Total: <strong>{fmt(total)} {formatUnit(unit)}</strong>
                  </div>
                </div>
              </Modal>
            );
          })()}
        </>
      )}

      {/* ── Surplus table ────────────────────────────────────────────────── */}
      {summaryView === 'surplus' && (
        <Table<FoulSurplusBackfillRow>
          className="foul-summary-table"
          dataSource={surplusBackfillData.surplus}
          columns={buildSurplusBackfillCols({
            runs: 'Surplus Runs', laterals: 'Surplus Lateral', manholes: 'Surplus Manholes', total: 'Total Surplus',
          })}
          rowKey="key"
          bordered
          size="middle"
          pagination={false}
          sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={5}><Typography.Text strong>TOTAL SURPLUS</Typography.Text></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>{surplusBackfillData.surplusTotal.toFixed(3)}</Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      )}

      {/* ── Backfill table ───────────────────────────────────────────────── */}
      {summaryView === 'backfill' && (
        <Table<FoulSurplusBackfillRow>
          className="foul-summary-table"
          dataSource={surplusBackfillData.backfill}
          columns={buildSurplusBackfillCols({
            runs: 'Backfill Runs', laterals: 'Backfill Laterals', manholes: 'Backfill Manholes', total: 'Total Backfill',
          })}
          rowKey="key"
          bordered
          size="middle"
          pagination={false}
          sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={5}><Typography.Text strong>TOTAL BACKFILL</Typography.Text></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>{surplusBackfillData.backfillTotal.toFixed(3)}</Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      )}

      {/* ── Surplus/Backfill formula modal ─────────────────────────────────── */}
      {surplusBackfillFormula && (
        <Modal
          title={`${surplusBackfillFormula.label} — Quantity Formula`}
          open={true}
          onCancel={() => setSurplusBackfillFormula(null)}
          footer={[<Button key="close" onClick={() => setSurplusBackfillFormula(null)}>Close</Button>]}
          width={560}
        >
          <div style={{ fontSize: 13, color: '#374151' }}>
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12, whiteSpace: 'pre-line' }}>
              {surplusBackfillFormula.formula}
            </div>
            <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 4 }}>
              Total: <strong>{surplusBackfillFormula.total.toFixed(3)} {formatUnit('m3')}</strong>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );


  return (
    <MainLayout canProceed={true} showFooter={false}>
      <Card
        className="foul-sewers-card shadow-sm"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: '8px', overflow: 'hidden' }}
      >
        {/* Sticky header + tabs */}
        <div
          className="foul-sewers-sticky-header"
          style={{
            position: 'sticky',
            top: '144px',
            backgroundColor: '#fff',
            zIndex: 98,
            borderRadius: '8px 8px 0 0',
            isolation: 'isolate',
          }}
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div className="flex justify-between items-center">
              <div>
                <Typography.Title level={3} style={{ fontWeight: 600, fontSize: '20px', color: '#111827', marginBottom: '8px' }}>
                  Foul Sewers
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: '14px', color: '#6B7280' }}>
                  Enter manhole and pipe run details for foul sewer systems
                </Typography.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {activeTab === 'manholes' && selectedManholeKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      deleteManholes(selectedManholeKeys);
                      setSelectedManholeKeys([]);
                    }, selectedManholeKeys.length)}
                    style={{ marginTop: '16px', height: '32px', borderRadius: '6px', fontWeight: 500 }}
                  >
                    Delete Selected ({selectedManholeKeys.length})
                  </Button>
                )}
                {activeTab === 'manholes' && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={openMhConfigModal}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: '16px',
                      height: '32px',
                      borderRadius: '6px',
                      fontWeight: 500,
                      borderColor: '#1890ff',
                      color: '#1890ff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    Configure
                  </Button>
                )}
                {activeTab === 'foulTakeOff' && selectedCombinedKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      const indices = combinedFoulTakeOffData
                        .filter((row) => selectedCombinedKeys.includes(row.id))
                        .map((row) => row.index);
                      deleteCombinedRows(indices);
                      setSelectedCombinedKeys([]);
                    }, selectedCombinedKeys.length)}
                    style={{ marginTop: '16px', height: '32px', borderRadius: '6px', fontWeight: 500 }}
                  >
                    Delete Selected ({selectedCombinedKeys.length})
                  </Button>
                )}
                {activeTab === 'foulTakeOff' && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={openRoadConstModal}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: '16px',
                      height: '32px',
                      borderRadius: '6px',
                      fontWeight: 500,
                      borderColor: '#1890ff',
                      color: '#1890ff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    Configure
                  </Button>
                )}
                {activeTab === 'pumpStation' && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={openPumpChamberModal}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: '16px',
                      height: '32px',
                      borderRadius: '6px',
                      fontWeight: 500,
                      borderColor: '#1890ff',
                      color: '#1890ff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    Configure
                  </Button>
                )}
                {showDevMode && (
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => setImportModalVisible(true)}
                    style={{ color: '#1890ff', fontWeight: 500 }}
                  >
                    Import Take-off
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Buttons */}
          <div className="foul-sewers-sticky-tabs px-6" style={{ paddingTop: '16px', paddingBottom: '0px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'flex-end', backgroundColor: '#fff' }}>
            {[
              { key: 'manholes', label: 'Manhole Schedule' },
              { key: 'foulTakeOff', label: 'Foul Take off' },
              { key: 'summary', label: 'Foul Summary' },
              { key: 'pumpStation', label: 'Pump Station' },
              { key: 'risingMain', label: 'Rising Main' },
            ].map((tab) => (
              <Button
                key={tab.key}
                type={activeTab === tab.key ? 'primary' : 'default'}
                onClick={() => handleTabChange(tab.key)}
                className={activeTab === tab.key ? 'active-tab-button' : 'inactive-tab-button'}
                style={{
                  backgroundColor: activeTab === tab.key ? COLORS.primary : '#E1E6EF',
                  borderColor: activeTab === tab.key ? COLORS.primary : '#E1E6EF',
                  color: activeTab === tab.key ? '#fff' : '#6B7280',
                  fontWeight: 500,
                  borderRadius: '6px 6px 0 0',
                  height: '38px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  borderBottom: activeTab === tab.key ? 'none' : '1px solid #E1E6EF',
                  position: 'relative',
                  zIndex: activeTab === tab.key ? 2 : 1,
                }}
              >
                {tab.label}
              </Button>
            ))}
            {activeTab === 'summary' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <Segmented
                  size="small"
                  value={summaryView}
                  onChange={(value) => setSummaryView(value as typeof summaryView)}
                  options={[
                    { label: 'Summary', value: 'summary' },
                    { label: 'Surplus', value: 'surplus' },
                    { label: 'Backfill', value: 'backfill' },
                  ]}
                />
                {summaryView === 'summary' && (
                  <ExpandCollapseAllButton
                    expanded={summaryAllKeys.every((k) => summaryExpandedKeys.includes(k))}
                    onToggle={() => {
                      const isFullyExpanded = summaryAllKeys.every((k) => summaryExpandedKeys.includes(k));
                      setSummaryExpandedKeys(isFullyExpanded ? [] : summaryAllKeys);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content section */}
        <div
          className="px-6 pb-6 foul-sewers-content-section"
          style={{ paddingTop: '0px', position: 'relative', zIndex: 1, marginTop: '0px' }}
        >

          {/* Tabbed Content */}
            {activeTab === 'manholes' && (isTableLoading ? (
              <TableLoadingPlaceholder />
            ) : (
              manholeScheduleContent
            ))}
            {activeTab === 'foulTakeOff' && (
              <div>
                {isTableLoading ? (
                  <TableLoadingPlaceholder />
                ) : (
                <Table
                  className="foul-sewers-table"
                  bordered
                  size="small"
                  scroll={{ x: 3735 }}
                  sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
                  pagination={false}
                  rowSelection={
                    state.isViewMode
                      ? undefined
                      : {
                          selectedRowKeys: selectedCombinedKeys,
                          onChange: setSelectedCombinedKeys,
                          fixed: true,
                        }
                  }
                  columns={combinedFoulTakeOffColumns}
                  dataSource={combinedFoulTakeOffData}
                  rowKey="id"
                />
                )}
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="text"
                    icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                    onClick={addCombinedRow}
                    style={{ color: COLORS.primary, fontWeight: 500, padding: '4px 8px' }}
                  >
                    Add New Row
                  </Button>
                </div>
              </div>
            )}
            {activeTab === 'summary' && summaryContent}
            {activeTab === 'pumpStation' && pumpStationContent}
            {activeTab === 'risingMain' && risingMainContent}

        </div>

        {/* Footer with Save & Continue button */}
        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "16px" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Rates Modal */}
      <Modal
        title="Manhole & Pipework Rates"
        open={ratesModalVisible}
        onCancel={() => setRatesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRatesModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={1200}
      >
        <RatesTable
          manholes={availableManholes}
          pipeRuns={foulSewersData.pipeRuns.table}
          laterals={foulSewersData.lateralConnections.table || []}
          projectName={state.projectName}
        />
      </Modal>

      {/* Excel Import Modal */}
      <ExcelImportModal
        onImport={handleUnifiedImport}
        onDownloadTemplate={handleDownloadTemplate}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      {/* Manhole Schedule Configure Modal */}
      <ColumnConfigModal
        title="Configure Manhole Schedule"
        open={mhConfigModalVisible}
        onClose={() => setMhConfigModalVisible(false)}
        onApply={applyMhConfig}
        width={380}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Set material type for all manholes" showDivider={false} columns={1}>
          <ConfigField label="Ladders">
            <Select
              value={mhConfigDraft.ladders}
              onChange={(v: MhLadderOption) => setMhConfigDraft((prev) => ({ ...prev, ladders: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="N/A">N/A</Select.Option>
              <Select.Option value="Stainless">Stainless</Select.Option>
              <Select.Option value="Galvanised">Galvanised</Select.Option>
            </Select>
          </ConfigField>
          <ConfigField label="Safety Chains">
            <Select
              value={mhConfigDraft.safetyChains}
              onChange={(v: MhLadderOption) => setMhConfigDraft((prev) => ({ ...prev, safetyChains: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="N/A">N/A</Select.Option>
              <Select.Option value="Stainless">Stainless</Select.Option>
              <Select.Option value="Galvanised">Galvanised</Select.Option>
            </Select>
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Road Construction Depth Configure Modal */}
      <ColumnConfigModal
        title="Configure Road Construction Depth"
        open={roadConstModalVisible}
        onClose={() => setRoadConstModalVisible(false)}
        onApply={applyRoadConstConfig}
        width={440}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Inputs" showDivider={false} columns={1}>
          <ConfigField label="Road Level (m)">
            <NumberInput
              value={roadConstDraft.roadLevel}
              onChange={(v) => updateRoadConstLevel('roadLevel', v ?? 0)}
              decimalPlaces={3}
              disabled={roadConstDepthOverridden}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Formation Level (m)">
            <NumberInput
              value={roadConstDraft.formationLevel}
              onChange={(v) => updateRoadConstLevel('formationLevel', v ?? 0)}
              decimalPlaces={3}
              disabled={roadConstDepthOverridden}
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Depth" showDivider={false} columns={1}>
          <ConfigField label="Road Construction Depth (m)">
            <Space.Compact style={{ width: '100%' }}>
              <NumberInput
                value={roadConstDraft.depth}
                onChange={(v) => setRoadConstDraft((prev) => ({ ...prev, depth: v ?? 0 }))}
                decimalPlaces={3}
                style={{ width: '100%' }}
              />
              <Tooltip title={roadConstDepthOverridden
                ? 'Manual override — click to reset to Road Level − Formation Level (re-enables those fields)'
                : 'Auto = Road Level − Formation Level'}
              >
                <Button
                  size="small"
                  type={roadConstDepthOverridden ? 'primary' : 'default'}
                  onClick={roadConstDepthOverridden ? clearRoadConstDepthOverride : undefined}
                  disabled={!roadConstDepthOverridden}
                  style={{ borderLeft: 0, fontSize: 11, fontWeight: 500, padding: '0 8px', height: 32 }}
                >
                  {roadConstDepthOverridden ? 'Manual' : 'Auto'}
                </Button>
              </Tooltip>
            </Space.Compact>
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Pump Chamber Configure Modal */}
      <ColumnConfigModal
        title="Configure"
        open={pumpChamberModalVisible}
        onClose={() => setPumpChamberModalVisible(false)}
        onApply={applyPumpChamberConfig}
        width={760}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Pumping Station Base" showDivider={false} columns={3}>
          <ConfigField label="MOT Type 1 Sub Base (mm)">
            <NumberInput
              value={pumpChamberDraft.motType1SubBaseMm ?? 0}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, motType1SubBaseMm: v ?? 0 }))}
              decimalPlaces={3}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <Select
              value={pumpChamberDraft.meshType}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, meshType: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="PAV2 Concrete Slab (mm)">
            <NumberInput
              value={pumpChamberDraft.pav2SlabMm ?? 0}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, pav2SlabMm: v ?? 0 }))}
              decimalPlaces={3}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Pump Chamber" showDivider={false} columns={3}>
          <ConfigField label="Concrete Blinding (mm)">
            <NumberInput
              value={pumpChamberDraft.pumpChamberBlindingMm ?? 0}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, pumpChamberBlindingMm: v ?? 0 }))}
              decimalPlaces={3}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <Select
              value={pumpChamberDraft.pumpChamberMeshType}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, pumpChamberMeshType: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Valve Chamber" showDivider={false} columns={3}>
          <ConfigField label="Width (m)">
            <NumberInput
              value={pumpChamberDraft.valveChamberWidth}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, valveChamberWidth: v ?? 0 }))}
              decimalPlaces={3}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Length (m)">
            <NumberInput
              value={pumpChamberDraft.valveChamberLength}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, valveChamberLength: v ?? 0 }))}
              decimalPlaces={3}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Depth (m)">
            <NumberInput
              value={pumpChamberDraft.valveChamberDepth}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, valveChamberDepth: v ?? 0 }))}
              decimalPlaces={3}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <Select
              value={pumpChamberDraft.valveChamberMeshType}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, valveChamberMeshType: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="Concrete in Base">
            <Select
              value={pumpChamberDraft.valveChamberConcreteGrade}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, valveChamberConcreteGrade: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {CONCRETE_GRADE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="Ladders — Supply / Install">
            <Select
              value={pumpChamberDraft.valveChamberLaddersService}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, valveChamberLaddersService: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {LADDER_SERVICE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Flow Meter Chamber" showDivider={false} columns={3}>
          <ConfigField label="Mesh Reinforcement">
            <Select
              value={pumpChamberDraft.flowMeterChamberMeshType}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, flowMeterChamberMeshType: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="Concrete in Base">
            <Select
              value={pumpChamberDraft.flowMeterChamberConcreteGrade}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, flowMeterChamberConcreteGrade: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {CONCRETE_GRADE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="Ladders — Supply / Install">
            <Select
              value={pumpChamberDraft.flowMeterChamberLaddersService}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, flowMeterChamberLaddersService: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {LADDER_SERVICE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Kiosk Base" showDivider={false} columns={3}>
          <ConfigField label="Mesh Reinforcement">
            <Select
              value={pumpChamberDraft.kioskBaseMeshType}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, kioskBaseMeshType: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="Concrete in Base">
            <Select
              value={pumpChamberDraft.kioskBaseConcreteGrade}
              onChange={(v: string) => setPumpChamberDraft((prev) => ({ ...prev, kioskBaseConcreteGrade: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {CONCRETE_GRADE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
        </ConfigSection>
        {/* <ConfigSection title="Pump Chamber" showDivider={false} columns={2}>
          <ConfigField label="Cover Level (m)">
            <NumberInput
              value={pumpChamberDraft.coverLevel}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, coverLevel: v ?? 0 }))}
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Invert (m)">
            <NumberInput
              value={pumpChamberDraft.invert}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, invert: v ?? 0 }))}
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Sump (m)">
            <NumberInput
              value={pumpChamberDraft.sump}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, sump: v ?? 0 }))}
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Chamber Dia (mm)">
            <NumberInput
              value={pumpChamberDraft.chamberDia}
              onChange={(v) => setPumpChamberDraft((prev) => ({ ...prev, chamberDia: v ?? 0 }))}
              decimalPlaces={0}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Pump Chamber — Calculated" showDivider={false} columns={2}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography.Text className="text-xs mb-1 block" style={{ lineHeight: '1.3', marginBottom: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Chamber Depth (m)
              <InfoCircleOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={() => setColCalcInfo({
                  title: 'Chamber Depth',
                  formula: 'Chamber Depth = Cover Level − Invert + Sump',
                  unit: 'm',
                  rows: [],
                })}
              />
            </Typography.Text>
            <div
              style={{
                marginTop: 'auto',
                padding: '4px 11px',
                background: '#f0f4ff',
                border: '1px solid #1890ff',
                borderRadius: 6,
                fontWeight: 700,
                color: '#1890ff',
                textAlign: 'right',
                height: 32,
                lineHeight: '22px',
              }}
            >
              {pumpChamberDepthCalc.toFixed(3)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography.Text className="text-xs mb-1 block" style={{ lineHeight: '1.3', marginBottom: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Depth Category (m)
              <InfoCircleOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={() => setColCalcInfo({
                  title: 'Depth Category',
                  formula: 'Depth Category = ROUND(Chamber Depth / 0.25) × 0.25',
                  unit: 'm',
                  rows: [],
                })}
              />
            </Typography.Text>
            <div
              style={{
                marginTop: 'auto',
                padding: '4px 11px',
                background: '#f0f4ff',
                border: '1px solid #1890ff',
                borderRadius: 6,
                fontWeight: 700,
                color: '#1890ff',
                textAlign: 'right',
                height: 32,
                lineHeight: '22px',
              }}
            >
              {pumpChamberDepthCategoryCalc.toFixed(3)}
            </div>
          </div>
        </ConfigSection> */}
      </ColumnConfigModal>

      <style>{`
        .build-up-collapse .ant-collapse-content-box {
          padding: 0 !important;
        }
        .build-up-table .build-up-header-row > td {
          background-color: #eff6ff !important;
          font-weight: 600 !important;
        }
        .build-up-collapse .ant-collapse-header {
          position: sticky !important;
          top: ${stickyOffset}px !important;
          z-index: 99 !important;
          background-color: #fafafa !important;
          border-bottom: 1px solid #d9d9d9;
        }
      `}</style>

      {/* Column calculation info modal — outside tabs so it renders on any active tab */}
      {colCalcInfo && (
        <Modal
          title={`${colCalcInfo.title} — Calculation`}
          open={true}
          onCancel={() => setColCalcInfo(null)}
          footer={[<Button key="close" onClick={() => setColCalcInfo(null)}>Close</Button>]}
          width={500}
        >
          <div style={{ fontSize: 13, color: '#374151' }}>
            <div style={{ padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
              {colCalcInfo.formula}
            </div>
          </div>
        </Modal>
      )}

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={pendingDeleteCount > 1 ? `Are you sure you want to delete ${pendingDeleteCount} records?` : undefined}
      />
    </MainLayout>
  );
};

export default FoulSewersTakeoff;
